import React, { useCallback, useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, TrendingUp, TrendingDown, Users, Clock, History, UserCheck, Shield, Phone, MessageSquare, CheckCircle2, XCircle, BarChart3, PieChart as PieChartIcon, Activity, Search, Filter, Download, MapPin, UserPlus, ChevronDown, ListFilter, RefreshCw, AlertCircle, Check, Tag, Heart, Sparkles, UserMinus, CreditCard, UserX } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ComposedChart, Scatter,
} from "recharts";
import toast from "react-hot-toast";
import { GetCustomerSupportAdmins, GetCustomerSupportCallingDetails, GetUnregisteredUsers, GetUnregisteredUsersStatusStats, GetActiveUsersDetails, GetCompletedUsers, GetHoldUsers, GetNotInterestedUsers, GetWebUnregisteredUsers } from "./apis/get";
import { PostBulkUpdateUnregisteredStatus, PatchAssignUserToSelf } from "./apis/post";
import { PatchUnholdUser } from "./apis/patch";
import { getUserId } from "../services/api";

const getInitialUserFilter = () => {
  const role = localStorage.getItem("role");
  if (!role) {
    return null;
  }
  if (role === "SUPER_ADMIN" || role === "ADMIN") {
    return "";
  }
  return getUserId();
};

const isSystemMessage = (inst: any) => {
  if (!inst || !inst.instruction) return true;
  const txt = inst.instruction.toLowerCase().trim();
  const isAuthorSystem = (inst.assigned_to || "").toLowerCase() === "system";
  const isAssignmentMsg = txt.includes("assigned to") || txt.includes("is assigned to");
  const isTestMsg = txt === "test";
  return isAuthorSystem || isAssignmentMsg || isTestMsg;
};

const isChatInstruction = (inst: any) => {
  if (isSystemMessage(inst)) return false;
  const txt = inst.instruction.toLowerCase().trim();
  if (txt.includes("call declined") || txt.includes("declined")) return false;
  if (txt.includes("busy, saying") && txt.includes("call back later")) return false;
  if (txt.includes("busy call later")) return false;
  return true;
};

export default function SalesTeamDashboard() {
  const role = localStorage.getItem("role");
  const isAdmin = role === "SUPER_ADMIN" || role === "ADMIN";
  const [activeMainTab, setActiveMainTab] = useState<"performance" | "unregistered" | "active-users" | "unassigned-active-users" | "hold-users" | "not-interested" | "store-data">("unregistered");
  const [storeData, setStoreData] = useState<any[]>([]);
  const [loadingStoreData, setLoadingStoreData] = useState(false);

  const fetchStoreData = useCallback(async () => {
    setLoadingStoreData(true);
    try {
      const response = await GetWebUnregisteredUsers();
      if (response && response.status === true) {
        setStoreData(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch store data:", error);
    } finally {
      setLoadingStoreData(false);
    }
  }, []);

  useEffect(() => {
    if (activeMainTab === "store-data") {
      fetchStoreData();
    }
  }, [fetchStoreData, activeMainTab]);

  const [holdUsers, setHoldUsers] = useState<any[]>([]);
  const [holdUsersTotal, setHoldUsersTotal] = useState(0);
  const [loadingHoldUsers, setLoadingHoldUsers] = useState(false);
  const [holdUsersLimit, setHoldUsersLimit] = useState(100);
  const [holdUsersOffset, setHoldUsersOffset] = useState(0);
  const [notInterestedUsers, setNotInterestedUsers] = useState<any[]>([]);
  const [notInterestedTotal, setNotInterestedTotal] = useState(0);
  const [loadingNotInterested, setLoadingNotInterested] = useState(false);
  const [notInterestedLimit, setNotInterestedLimit] = useState(100);
  const [notInterestedOffset, setNotInterestedOffset] = useState(0);
  const [completedUsers, setCompletedUsers] = useState<any[]>([]);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [loadingCompletedUsers, setLoadingCompletedUsers] = useState(false);
  const [completedLimit, setCompletedLimit] = useState(10);
  const [completedOffset, setCompletedOffset] = useState(0);
  const [completedSearchQuery, setCompletedSearchQuery] = useState("");
  const [debouncedCompletedSearch, setDebouncedCompletedSearch] = useState("");
  const [isAssigning, setIsAssigning] = useState<string | null>(null);
  const [supportAdmins, setSupportAdmins] = useState<any[]>([]);
  const [selectedAdmin, setSelectedAdmin] = useState<string>("");
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<string>("current");
  const [callingDetails, setCallingDetails] = useState<any>(null);
  const [activeUsersDetails, setActiveUsersDetails] = useState<any>(null);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [selectedActiveAdmin, setSelectedActiveAdmin] = useState<string | null>(() => {
    const filter = getInitialUserFilter();
    return filter === "" ? "Soniya Rathi" : filter;
  });
  const [isActiveAdminDropdownOpen, setIsActiveAdminDropdownOpen] = useState(false);
  const [dismissedUserIds, setDismissedUserIds] = useState<Set<string>>(new Set());
  const [dismissedAgents, setDismissedAgents] = useState<Set<string>>(new Set());
  const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null);
  const [activeUserStatusFilter, setActiveUserStatusFilter] = useState<"all" | "paid" | "failed" | "pending">("all");
  const [isActiveStatusDropdownOpen, setIsActiveStatusDropdownOpen] = useState(false);
  const [premiumFilter, setPremiumFilter] = useState<"all" | "premium" | "non-premium">("all");
  const [isPremiumDropdownOpen, setIsPremiumDropdownOpen] = useState(false);
  const [specialPrefFilter, setSpecialPrefFilter] = useState<"all" | "special" | "no-special">("all");
  const [isSpecialPrefDropdownOpen, setIsSpecialPrefDropdownOpen] = useState(false);
  const [interactionFilter, setInteractionFilter] = useState<"all" | "talked" | "not-talked">("all");
  const [isInteractionDropdownOpen, setIsInteractionDropdownOpen] = useState(false);
  const [activeUserTimeFilter, setActiveUserTimeFilter] = useState<"all" | "today" | "yesterday" | "weekly" | "half_monthly" | "monthly" | "quarterly" | "yearly">("all");
  const [isActiveTimeDropdownOpen, setIsActiveTimeDropdownOpen] = useState(false);
  const [paymentTimeFilter, setPaymentTimeFilter] = useState<"all" | "today" | "yesterday" | "weekly" | "half_monthly" | "monthly">("all");
  const [isPaymentTimeDropdownOpen, setIsPaymentTimeDropdownOpen] = useState(false);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());

  // Unregistered Users State
  const [unregisteredUsers, setUnregisteredUsers] = useState<any[]>([]);
  const [unregLoading, setUnregLoading] = useState(false);
  const [unregLimit, setUnregLimit] = useState(50);
  const [unregOffset, setUnregOffset] = useState(0);
  const [unregFilterUser, setUnregFilterUser] = useState(getInitialUserFilter());
  const [unregFilterStatus, setUnregFilterStatus] = useState("");
  const [unregFilterTag, setUnregFilterTag] = useState("unregister user");
  const [unregTotal, setUnregTotal] = useState(0);
  const [unregHasTotal, setUnregHasTotal] = useState(false);
  const [unregStartDate, setUnregStartDate] = useState("");
  const [unregEndDate, setUnregEndDate] = useState("");
  const currentLimit = (!unregStartDate && !unregEndDate) ? 100 : unregLimit;
  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [isLimitDropdownOpen, setIsLimitDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [unregFilterOnboarded, setUnregFilterOnboarded] = useState<"all" | "onboarded" | "pending">("all");
  const [isOnboardedDropdownOpen, setIsOnboardedDropdownOpen] = useState(false);

  // Unregistered Stats State
  const [unregStats, setUnregStats] = useState<any>(null);
  const [loadingUnregStats, setLoadingUnregStats] = useState(false);

  // Bulk Update State
  const [bulkFromStatus, setBulkFromStatus] = useState("pending");
  const [bulkToStatus, setBulkToStatus] = useState("Interested");
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const statusOptions = ["pending", "Busy Call Later", "Interested", "Not Interested", "Declined", "Married/Engaged"];

  const toggleDropdown = (dropdownName: string) => {
    // Active Users tab dropdowns
    setIsActiveTimeDropdownOpen(prev => dropdownName === "activeTime" ? !prev : false);
    setIsActiveStatusDropdownOpen(prev => dropdownName === "activeStatus" ? !prev : false);
    setIsPremiumDropdownOpen(prev => dropdownName === "premium" ? !prev : false);
    setIsSpecialPrefDropdownOpen(prev => dropdownName === "specialPref" ? !prev : false);
    setIsInteractionDropdownOpen(prev => dropdownName === "interaction" ? !prev : false);
    setIsPaymentTimeDropdownOpen(prev => dropdownName === "paymentTime" ? !prev : false);
    setIsActiveAdminDropdownOpen(prev => dropdownName === "activeAdmin" ? !prev : false);

    // Unregistered Users tab dropdowns
    setIsTagDropdownOpen(prev => dropdownName === "unregTag" ? !prev : false);
    setIsAgentDropdownOpen(prev => dropdownName === "unregAgent" ? !prev : false);
    setIsStatusDropdownOpen(prev => dropdownName === "unregStatus" ? !prev : false);
    setIsOnboardedDropdownOpen(prev => dropdownName === "unregOnboarded" ? !prev : false);
    setIsLimitDropdownOpen(prev => dropdownName === "unregLimit" ? !prev : false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".dropdown-container")) {
        setIsActiveTimeDropdownOpen(false);
        setIsActiveStatusDropdownOpen(false);
        setIsPremiumDropdownOpen(false);
        setIsSpecialPrefDropdownOpen(false);
        setIsInteractionDropdownOpen(false);
        setIsPaymentTimeDropdownOpen(false);
        setIsActiveAdminDropdownOpen(false);
        setIsTagDropdownOpen(false);
        setIsAgentDropdownOpen(false);
        setIsStatusDropdownOpen(false);
        setIsOnboardedDropdownOpen(false);
        setIsLimitDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [loadingSupport, setLoadingSupport] = useState(false);
  const [loadingCallingDetails, setLoadingCallingDetails] = useState(false);

  const fetchCallingDetails = useCallback(async (username: string, period: string) => {
    if (!username) return;
    setLoadingCallingDetails(true);
    try {
      const response = await GetCustomerSupportCallingDetails(username, period);
      if (response.success || response.status === "success" || response.status === true) {
        setCallingDetails(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch calling details:", error);
    } finally {
      setLoadingCallingDetails(false);
    }
  }, []);

  const fetchSupportAdmins = useCallback(async () => {
    setLoadingSupport(true);
    try {
      const response = await GetCustomerSupportAdmins();
      let adminData = [];
      if (Array.isArray(response)) adminData = response;
      else if (response && Array.isArray(response.data)) adminData = response.data;
      else if (response && response.data && Array.isArray(response.data.data)) adminData = response.data.data;
      else if (response && response.status === "success" && Array.isArray(response.data)) adminData = response.data;

      const excluded = ["soniyarathi"];
      adminData = adminData.filter((admin: any) => admin.username && !excluded.includes(admin.username.toLowerCase()));


      const hasSoniya = adminData.some((admin: any) => {
        const name = (admin.username || "").toLowerCase();
        return name === "soniya" || name === "soniya rathi";
      });
      if (!hasSoniya) {
        adminData.push({ id: "soniya_rathi_manual", username: "Soniya Rathi", role: "Support Agent" });
      }

      setSupportAdmins(adminData);
      if (adminData.length > 0) {
        const loggedInUser = getUserId();
        const exists = adminData.some((admin: any) => admin.username === loggedInUser);
        const defaultAdmin = exists ? loggedInUser : adminData[0].username;
        if (!selectedAdmin) {
          setSelectedAdmin(defaultAdmin);
        }
        if (selectedActiveAdmin === null) {
          if (exists) {
            setSelectedActiveAdmin(loggedInUser);
          } else {
            const soniyaAdmin = adminData.find((admin: any) => {
              const name = (admin.username || "").toLowerCase();
              return name === "soniya" || name === "soniya rathi" || name === "soniyarathi";
            });
            setSelectedActiveAdmin(soniyaAdmin ? soniyaAdmin.username : "Soniya Rathi");
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch support admins:", error);
    } finally {
      setLoadingSupport(false);
    }
  }, []);

  const fetchUnregStats = useCallback(async (period: string = "all") => {
    setLoadingUnregStats(true);
    try {
      const response = await GetUnregisteredUsersStatusStats(period);
      if (response.overall || response.data?.overall) {
        setUnregStats(response.data || response);
      }
    } catch (error) {
      console.error("Failed to fetch unregistered stats:", error);
    } finally {
      setLoadingUnregStats(false);
    }
  }, []);

  const fetchUnregisteredUsers = useCallback(async (limit = currentLimit, offset = unregOffset) => {
    setUnregLoading(true);
    try {
      const response = await GetUnregisteredUsers({
        limit: limit,
        offset: offset,
        current_user: unregFilterUser,
        assigned_to: unregFilterUser,
        status: unregFilterStatus,
        tag: unregFilterTag,
        start_date: unregStartDate,
        end_date: unregEndDate
      });
      if (response.success || response.status === "success" || response.status === true) {
        const users = response.users || response.data?.users || response.data || [];
        setUnregisteredUsers(users);
        const hasTotal = typeof response.total_count === "number" || typeof response.data?.total_count === "number";
        setUnregHasTotal(hasTotal);
        setUnregTotal(hasTotal ? (response.total_count || response.data?.total_count || 0) : users.length);
      }
    } catch (error) {
      console.error("Failed to fetch unregistered users:", error);
    } finally {
      setUnregLoading(false);
    }
  }, [currentLimit, unregOffset, unregFilterUser, unregFilterStatus, unregFilterTag, unregStartDate, unregEndDate]);
  const fetchActiveUsersDetails = useCallback(async () => {
    if (selectedActiveAdmin === null || selectedActiveAdmin === undefined) return;
    setLoadingActiveUsers(true);
    try {
      const response = await GetActiveUsersDetails(
        selectedActiveAdmin,
        10000,
        0,
        "" // Always fetch all active users, frontend will handle the time-based filtering
      );
      if (response.status === "success" || response.status === true) {
        if (response.data && Array.isArray(response.data)) {
          const mergedGroups: { [key: string]: any } = {};
          response.data.forEach((group: any) => {
            const originalKey = group.assigned_to || "Unassigned";
            const normKey = originalKey.toLowerCase();
            if (!mergedGroups[normKey]) {
              mergedGroups[normKey] = {
                ...group,
                assigned_to: originalKey,
                users: []
              };
            }
            if (Array.isArray(group.users)) {
              mergedGroups[normKey].users.push(...group.users);
            }
          });
          response.data = Object.values(mergedGroups);
        }
        setActiveUsersDetails(response);
      }
    } catch (error) {
      console.error("Failed to fetch active users details:", error);
    } finally {
      setLoadingActiveUsers(false);
    }
  }, [selectedActiveAdmin]);

  const handleRefreshActiveUsers = useCallback(() => {
    fetchActiveUsersDetails();
  }, [fetchActiveUsersDetails]);

  useEffect(() => {
    if (activeMainTab === "active-users" || activeMainTab === "unregistered") {
      fetchActiveUsersDetails();
    }
  }, [fetchActiveUsersDetails, activeMainTab]);

  const fetchNotInterestedUsers = useCallback(async (limit = notInterestedLimit, offset = notInterestedOffset) => {
    setLoadingNotInterested(true);
    try {
      const response = await GetNotInterestedUsers(limit, offset);
      if (response && (response.status === true || response.status === "success" || response.success)) {
        setNotInterestedUsers(response.data || []);
        setNotInterestedTotal(response.count || (response.data || []).length);
      }
    } catch (error) {
      console.error("Failed to fetch not interested users:", error);
    } finally {
      setLoadingNotInterested(false);
    }
  }, [notInterestedLimit, notInterestedOffset]);

  const fetchHoldUsers = useCallback(async (limit = holdUsersLimit, offset = holdUsersOffset) => {
    setLoadingHoldUsers(true);
    try {
      const response = await GetHoldUsers(limit, offset);
      if (response && (response.status === true || response.status === "success" || response.success)) {
        setHoldUsers(response.data || []);
        setHoldUsersTotal(response.total_count || (response.data || []).length);
      }
    } catch (error) {
      console.error("Failed to fetch hold users:", error);
    } finally {
      setLoadingHoldUsers(false);
    }
  }, [holdUsersLimit, holdUsersOffset]);

  const handleUnholdUser = async (userId: string) => {
    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    setLoadingHoldUsers(true);
    try {
      const response = await PatchUnholdUser(userId);
      if (response && (response.status === true || response.status === "success" || response.success)) {
        toast.success(response.message || "User unheld successfully");
        const nextOffset = holdUsers.length === 1 && holdUsersOffset > 0
          ? Math.max(0, holdUsersOffset - holdUsersLimit)
          : holdUsersOffset;
        setHoldUsersOffset(nextOffset);
        await fetchHoldUsers(holdUsersLimit, nextOffset);
      } else {
        toast.error(response?.message || "Failed to unhold user");
        setLoadingHoldUsers(false);
      }
    } catch (error) {
      console.error("Failed to unhold user:", error);
      toast.error("An error occurred while unholding user");
      setLoadingHoldUsers(false);
    }
  };

  useEffect(() => {
    if (activeMainTab === "hold-users") {
      fetchHoldUsers(holdUsersLimit, holdUsersOffset);
    }
  }, [fetchHoldUsers, activeMainTab, holdUsersLimit, holdUsersOffset]);

  useEffect(() => {
    if (activeMainTab === "not-interested") {
      fetchNotInterestedUsers(notInterestedLimit, notInterestedOffset);
    }
  }, [fetchNotInterestedUsers, activeMainTab, notInterestedLimit, notInterestedOffset]);

  const fetchCompletedUsers = useCallback(async (limit = completedLimit, offset = completedOffset, search = debouncedCompletedSearch) => {
    setLoadingCompletedUsers(true);
    try {
      const response = await GetCompletedUsers(limit, offset, "", (search || "").toLowerCase());
      if (response && response.status === "success") {
        setCompletedUsers(response.data || []);
        setCompletedTotal(response.total_count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch completed users:", error);
    } finally {
      setLoadingCompletedUsers(false);
    }
  }, [completedLimit, completedOffset, debouncedCompletedSearch]);

  const handleAssignToSelf = async (userId: string) => {
    setIsAssigning(userId);
    const loadingToast = toast.loading("Assigning profile to yourself...");
    try {
      const response = await PatchAssignUserToSelf(userId);
      if (response && (response.status === "success" || response.status === true || response.success)) {
        toast.success("Profile assigned to you successfully!", { id: loadingToast });
        fetchCompletedUsers(completedLimit, completedOffset);
        fetchActiveUsersDetails();
      } else {
        toast.error(response?.message || response?.detail || "Failed to assign profile", { id: loadingToast });
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred during assignment", { id: loadingToast });
    } finally {
      setIsAssigning(null);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCompletedSearch(completedSearchQuery);
    }, 400);
    return () => clearTimeout(handler);
  }, [completedSearchQuery]);

  useEffect(() => {
    if (activeMainTab === "unassigned-active-users") {
      fetchCompletedUsers(completedLimit, completedOffset, debouncedCompletedSearch);
    }
  }, [fetchCompletedUsers, activeMainTab, completedLimit, completedOffset, debouncedCompletedSearch]);

  const filteredCompletedUsers = useMemo(() => {
    if (!completedSearchQuery) return completedUsers;
    const query = completedSearchQuery.toLowerCase().trim();
    return completedUsers.filter((user: any) => {
      const name = String(user.profile_details?.name || user.username || "").toLowerCase();
      const userId = String(user.user_id || "").toLowerCase();
      const phone = String(user.phone_number || user.phone || "").toLowerCase();
      const email = String(user.email_address || user.email || "").toLowerCase();
      const city = String(user.profile_details?.city || "").toLowerCase();
      const state = String(user.profile_details?.state || "").toLowerCase();
      const caste = String(user.profile_details?.caste || "").toLowerCase();
      const dob = String(user.profile_details?.date_of_birth || "").toLowerCase();
      const gender = String(user.gender || user.profile_details?.gender || "").toLowerCase();
      return (
        name.includes(query) ||
        userId.includes(query) ||
        phone.includes(query) ||
        email.includes(query) ||
        city.includes(query) ||
        state.includes(query) ||
        caste.includes(query) ||
        dob.includes(query) ||
        gender.includes(query)
      );
    });
  }, [completedUsers, completedSearchQuery]);

  const sortedSupportAdmins = useMemo(() => {
    const priorityNames = ["Akshat", "Mamta", "Soniya Rathi", "Soniya"];
    return [...supportAdmins].sort((a, b) => {
      const aName = a.username || "";
      const bName = b.username || "";

      const aPriority = priorityNames.indexOf(aName);
      const bPriority = priorityNames.indexOf(bName);

      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;

      return aName.localeCompare(bName);
    });
  }, [supportAdmins]);

  const getAssignmentDate = useCallback((user: any): Date | null => {
    if (!user.instructions || !Array.isArray(user.instructions)) {
      return user.created_at ? new Date(user.created_at) : null;
    }
    const assignAdmin = (user.assigned_to || "").toLowerCase();
    let assignInstructions = [];
    if (assignAdmin) {
      assignInstructions = user.instructions.filter((inst: any) => {
        if (!inst.instruction) return false;
        const txt = inst.instruction.toLowerCase();
        return txt.includes("assigned to ") && txt.includes(assignAdmin);
      });
    }

    if (assignInstructions.length > 0) {
      assignInstructions.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
      return new Date(assignInstructions[0].date);
    }

    // Fallback: if any other instruction exists, use the earliest instruction's date
    const validInstructions = user.instructions.filter((inst: any) => inst.date);
    if (validInstructions.length > 0) {
      validInstructions.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
      return new Date(validInstructions[0].date);
    }

    return user.created_at ? new Date(user.created_at) : null;
  }, []);

  const isDateInPeriod = useCallback((dateStr: string, period: string): boolean => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;

    const now = new Date();
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    const yesterdayMidnight = new Date(todayMidnight);
    yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1);

    const time = d.getTime();

    switch (period) {
      case "today":
        return time >= todayMidnight.getTime();
      case "yesterday":
        return time >= yesterdayMidnight.getTime() && time < todayMidnight.getTime();
      case "weekly": {
        const diffDays = (now.getTime() - time) / (1000 * 60 * 60 * 24);
        return diffDays <= 7;
      }
      case "monthly": {
        const diffDays = (now.getTime() - time) / (1000 * 60 * 60 * 24);
        return diffDays <= 30;
      }
      case "quarterly": {
        const diffDays = (now.getTime() - time) / (1000 * 60 * 60 * 24);
        return diffDays <= 90;
      }
      case "yearly": {
        const diffDays = (now.getTime() - time) / (1000 * 60 * 60 * 24);
        return diffDays <= 365;
      }
      case "half_monthly": {
        const diffDays = (now.getTime() - time) / (1000 * 60 * 60 * 24);
        return diffDays <= 15;
      }
      default:
        return true; // "all"
    }
  }, []);

  const filteredActiveUsers = useMemo(() => {
    if (!activeUsersDetails?.data) return [];

    const norm = selectedActiveAdmin?.toLowerCase();
    const normActiveAdmin = (norm === "soniya rathi" || norm === "soniyarathi") ? "soniya" : norm;

    const isUserPremium = (u: any) => {
      return (u.transactions?.success_details || []).some((tx: any) => {
        if ((tx.credits || 0) < 25) return false;
        const txTime = new Date(tx.date).getTime();
        const now = new Date();
        const diffDays = (now.getTime() - txTime) / (1000 * 60 * 60 * 24);
        return diffDays <= 90;
      });
    };

    const getLatestTxDate = (u: any) => {
      const successTx = u.transactions?.success_details || [];
      if (successTx.length === 0) return 0;
      const dates = successTx.map((t: any) => t.date ? new Date(t.date).getTime() : 0);
      return Math.max(...dates);
    };

    return activeUsersDetails.data
      .filter((group: any) => !dismissedAgents.has(group.assigned_to || "Unassigned"))
      .filter((group: any) => {
        if (!normActiveAdmin) return true;
        const groupAdmin = (group.assigned_to || "").toLowerCase();
        if (normActiveAdmin === "soniya") {
          return groupAdmin === "soniya" || groupAdmin === "soniya rathi" || groupAdmin === "soniyarathi";
        }
        return groupAdmin === normActiveAdmin;
      })
      .map((group: any) => {
        const filteredUsers = group.users.filter((u: any) => {
          if (dismissedUserIds.has(u.user_id)) return false;

          const assignDate = getAssignmentDate({ ...u, assigned_to: group.assigned_to });
          const assignDateStr = assignDate ? assignDate.toISOString() : null;
          if (activeUserTimeFilter !== "all" && (!assignDateStr || !isDateInPeriod(assignDateStr, activeUserTimeFilter))) {
            return false;
          }

          const txnFilterPeriod = paymentTimeFilter !== "all" ? paymentTimeFilter : activeUserTimeFilter;
          const allTransactions = [
            ...(u.transactions?.success_details || []).map((t: any) => ({ ...t, type: 'paid' })),
            ...(u.transactions?.failed_details || []).map((t: any) => ({ ...t, type: 'failed' })),
            ...(u.transactions?.pending_details || []).map((t: any) => ({ ...t, type: 'pending' }))
          ]
            .filter((t: any) => isDateInPeriod(t.date, txnFilterPeriod))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

          const latestStatus = allTransactions.length > 0 ? allTransactions[0].type : 'none';

          if (paymentTimeFilter !== "all" && allTransactions.length === 0) {
            return false;
          }

          let statusMatches = true;
          if (activeUserStatusFilter !== "all") {
            if (activeUserStatusFilter === "paid") {
              statusMatches = (u.transactions?.success_details || []).some((t: any) => {
                const credits = t.credits || 0;
                if (credits >= 25) {
                  const txTime = new Date(t.date).getTime();
                  const now = new Date();
                  const diffDays = (now.getTime() - txTime) / (1000 * 60 * 60 * 24);
                  return diffDays <= 90 && isDateInPeriod(t.date, txnFilterPeriod);
                }
                return isDateInPeriod(t.date, txnFilterPeriod);
              });
            } else {
              statusMatches = latestStatus === activeUserStatusFilter;
            }
          }

          if (!statusMatches) return false;

          if (premiumFilter === "premium" && !isUserPremium(u)) {
            return false;
          }
          if (premiumFilter === "non-premium" && isUserPremium(u)) {
            return false;
          }

          if (specialPrefFilter === "special" && !u.special_preference_notes_exists) {
            return false;
          }
          if (specialPrefFilter === "no-special" && u.special_preference_notes_exists) {
            return false;
          }

          const hasChat = (u.instructions || []).some((inst: any) => isChatInstruction(inst) && isDateInPeriod(inst.date, activeUserTimeFilter));
          if (interactionFilter === "talked" && !hasChat) {
            return false;
          }
          if (interactionFilter === "not-talked" && hasChat) {
            return false;
          }

          return true;
        });

        const sortedUsers = [...filteredUsers].sort((a: any, b: any) => {
          return getLatestTxDate(b) - getLatestTxDate(a);
        });

        return {
          ...group,
          users: sortedUsers
        };
      })
      .filter((group: any) => group.users.length > 0);
  }, [activeUsersDetails, selectedActiveAdmin, activeUserStatusFilter, dismissedAgents, dismissedUserIds, activeUserTimeFilter, paymentTimeFilter, isDateInPeriod, getAssignmentDate, premiumFilter, specialPrefFilter, interactionFilter]);

  const activeUsersSummary = useMemo(() => {
    const summary = {
      total: 0,
      success: 0,
      failed: 0,
      pending: 0,
      interacted: 0,
      notInteracted: 0,
      chats: 0,
      awaitingFollowup: 0,
      premium: 0,
      nonPremium: 0,
      hasSpecialPref: 0,
      noSpecialPref: 0
    };

    filteredActiveUsers.forEach((group: any) => {
      group.users.forEach((user: any) => {
        summary.total++;

        if (user.awaiting_followup && Array.isArray(user.awaiting_followup) && user.awaiting_followup.length > 0) {
          summary.awaitingFollowup++;
        }

        const chatInsts = (user.instructions || []).filter((inst: any) =>
          isChatInstruction(inst) && isDateInPeriod(inst.date, activeUserTimeFilter)
        );

        if (chatInsts.length > 0) {
          summary.interacted++;
        } else {
          summary.notInteracted++;
        }
        summary.chats += chatInsts.length;

        if (user.special_preference_notes_exists) {
          summary.hasSpecialPref++;
        } else {
          summary.noSpecialPref++;
        }

        const isPremium = user.transactions?.success_details?.some((tx: any) => {
          if ((tx.credits || 0) < 25) return false;
          const txTime = new Date(tx.date).getTime();
          const now = new Date();
          const diffDays = (now.getTime() - txTime) / (1000 * 60 * 60 * 24);
          return diffDays <= 90;
        });
        if (isPremium) {
          summary.premium++;
        } else {
          summary.nonPremium++;
        }

        // Use the same logic as the filter to find the most recent status
        const txnFilterPeriod = paymentTimeFilter !== "all" ? paymentTimeFilter : activeUserTimeFilter;
        const allTransactions = [
          ...(user.transactions?.success_details || []).map((t: any) => ({ ...t, type: 'paid' })),
          ...(user.transactions?.failed_details || []).map((t: any) => ({ ...t, type: 'failed' })),
          ...(user.transactions?.pending_details || []).map((t: any) => ({ ...t, type: 'pending' }))
        ]
          .filter((t: any) => isDateInPeriod(t.date, txnFilterPeriod))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        const latestStatus = allTransactions.length > 0 ? allTransactions[0].type : 'none';

        const hasSuccess = (user.transactions?.success_details || []).some((t: any) => {
          const credits = t.credits || 0;
          if (credits >= 25) {
            const txTime = new Date(t.date).getTime();
            const now = new Date();
            const diffDays = (now.getTime() - txTime) / (1000 * 60 * 60 * 24);
            return diffDays <= 90 && isDateInPeriod(t.date, txnFilterPeriod);
          }
          return isDateInPeriod(t.date, txnFilterPeriod);
        });
        if (hasSuccess) summary.success++;
        else if (latestStatus === 'failed') summary.failed++;
        else if (latestStatus === 'pending') summary.pending++;
      });
    });

    return summary;
  }, [filteredActiveUsers, activeUserTimeFilter, paymentTimeFilter, isDateInPeriod]);



  const periodLabelText = useMemo(() => {
    switch (activeUserTimeFilter) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "weekly":
        return "Past 7 Days";
      case "half_monthly":
        return "Past 15 Days";
      case "monthly":
        return "Past 30 Days";
      case "quarterly":
        return "Past 90 Days";
      case "yearly":
        return "Past 365 Days";
      default:
        return "All Assignments";
    }
  }, [activeUserTimeFilter]);

  const paymentPeriodLabelText = useMemo(() => {
    switch (paymentTimeFilter) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "weekly":
        return "Past 7 Days";
      case "half_monthly":
        return "Past 15 Days";
      case "monthly":
        return "Past 30 Days";
      default:
        return periodLabelText;
    }
  }, [paymentTimeFilter, periodLabelText]);

  const totalActiveUsersCount = useMemo(() => {
    if (!activeUsersDetails?.data) return 0;
    const norm = selectedActiveAdmin?.toLowerCase();
    const normActiveAdmin = (norm === "soniya rathi" || norm === "soniyarathi") ? "soniya" : norm;
    return activeUsersDetails.data
      .filter((group: any) => {
        if (!normActiveAdmin) return true;
        const groupAdmin = (group.assigned_to || "").toLowerCase();
        if (normActiveAdmin === "soniya") {
          return groupAdmin === "soniya" || groupAdmin === "soniya rathi" || groupAdmin === "soniyarathi";
        }
        return groupAdmin === normActiveAdmin;
      })
      .reduce((sum: number, group: any) => sum + (group.users?.length || 0), 0);
  }, [activeUsersDetails, selectedActiveAdmin]);

  const activeUsersPieData = useMemo(() => {
    return [
      { name: 'Success', value: activeUsersSummary.success, color: '#10b981' },
      { name: 'Failed', value: activeUsersSummary.failed, color: '#ef4444' },
      { name: 'Pending', value: activeUsersSummary.pending, color: '#f59e0b' },
    ].filter(i => i.value > 0);
  }, [activeUsersSummary]);

  const activeUsersLineData = useMemo(() => {
    const dateMap: { [key: string]: { date: string, name: string, success: number, failed: number } } = {};

    filteredActiveUsers.forEach((group: any) => {
      group.users.forEach((user: any) => {
        const processTx = (tx: any, type: 'success' | 'failed') => {
          if (!tx.date) return;
          const isoDate = tx.date.split('T')[0];
          if (!dateMap[isoDate]) {
            dateMap[isoDate] = {
              date: isoDate,
              name: new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              success: 0,
              failed: 0
            };
          }
          dateMap[isoDate][type]++;
        };

        user.transactions.success_details.forEach((tx: any) => processTx(tx, 'success'));
        user.transactions.failed_details.forEach((tx: any) => processTx(tx, 'failed'));
      });
    });

    return Object.values(dateMap)
      .sort((a: any, b: any) => a.date.localeCompare(b.date))
      .slice(-10);
  }, [filteredActiveUsers]);

  const aggregateSupportData = useMemo(() => {
    if (!filteredActiveUsers.length) return { charts: [], coverage: [] };

    const dateMap: { [key: string]: any } = {};
    let supportedUsers = 0;
    let totalUsersCount = 0;

    const getSafeDateStr = (dateInput: any) => {
      if (!dateInput) return null;
      const d = new Date(dateInput);
      if (isNaN(d.getTime())) return null;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    filteredActiveUsers.forEach((group: any) => {
      if (!group.users) return;

      group.users.forEach((user: any) => {
        totalUsersCount++;

        const chatSupportInstructions = (user.instructions || []).filter((inst: any) => isChatInstruction(inst));

        if (chatSupportInstructions.length > 0) {
          supportedUsers++;
        }

        const userTalkedDates = new Set<string>();
        chatSupportInstructions.forEach((inst: any) => {
          if (activeUserTimeFilter !== "all" && !isDateInPeriod(inst.date, activeUserTimeFilter)) {
            return;
          }
          const dStr = getSafeDateStr(inst.date);
          if (!dStr) return;
          userTalkedDates.add(dStr);
        });

        userTalkedDates.forEach((dStr) => {
          if (!dateMap[dStr]) dateMap[dStr] = { date: dStr, name: new Date(dStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), chats: 0, conversions: 0 };
          dateMap[dStr].chats++;
        });

        const txnFilterPeriod = paymentTimeFilter !== "all" ? paymentTimeFilter : activeUserTimeFilter;
        (user.transactions?.success_details || []).forEach((tx: any) => {
          if (txnFilterPeriod !== "all" && !isDateInPeriod(tx.date, txnFilterPeriod)) {
            return;
          }
          const dStr = getSafeDateStr(tx.date);
          if (!dStr) return;
          if (!dateMap[dStr]) dateMap[dStr] = { date: dStr, name: new Date(dStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), chats: 0, conversions: 0 };
          dateMap[dStr].conversions++;
        });
      });
    });

    const coverage = [
      { name: 'Supported', value: supportedUsers, color: '#6366f1' },
      { name: 'No Interaction', value: Math.max(0, totalUsersCount - supportedUsers), color: '#334155' }
    ];

    const charts = Object.values(dateMap)
      .sort((a: any, b: any) => a.date.localeCompare(b.date))
      .slice(-20);

    return { charts, coverage };
  }, [filteredActiveUsers, activeUserTimeFilter, paymentTimeFilter, isDateInPeriod]);

  const userJourneyData = useMemo(() => {
    if (!selectedUserDetails) return [];

    const events: any[] = [];

    // Support Chat Events
    (selectedUserDetails.instructions || []).forEach((i: any) => {
      if (isChatInstruction(i)) {
        events.push({ date: new Date(i.date), type: 'chat' });
      }
    });

    // Match Events
    (selectedUserDetails.matches || []).forEach((m: any) => {
      events.push({ date: new Date(m.created_at), type: m.published ? 'publishedMatch' : 'notPublishedMatch' });
    });

    // Transaction Events
    (selectedUserDetails.transactions?.success_details || []).forEach((t: any) => {
      events.push({ date: new Date(t.date), type: 'paid' });
    });

    // Interest/Like/View Events
    (selectedUserDetails.likes || []).forEach((l: any) => {
      const eventDate = l.created_at || l.date || l.interaction_date;
      if (eventDate) {
        const type = l.liked ? 'liked' : l.disliked ? 'disliked' : 'viewed';
        events.push({ date: new Date(eventDate), type });
      }
    });

    if (events.length === 0) return [];

    // Sort all events by date
    events.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Group by day for the chart
    const dateMap: { [key: string]: any } = {};

    // Helper to get local YYYY-MM-DD
    const getLocalDateStr = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Fill in dates between first and last event
    const start = new Date(events[0].date);
    const end = new Date(events[events.length - 1].date);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = getLocalDateStr(d);
      dateMap[dateStr] = {
        date: dateStr,
        name: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        chat: 0,
        publishedMatch: 0,
        notPublishedMatch: 0,
        liked: 0,
        disliked: 0,
        viewed: 0,
        paid: 0,
        paidGraph: 0,
        activity: 0
      };
    }

    events.forEach(e => {
      const dateStr = getLocalDateStr(e.date);
      if (dateMap[dateStr]) {
        if (e.type === 'chat') dateMap[dateStr].chat++;
        else if (e.type === 'publishedMatch') dateMap[dateStr].publishedMatch++;
        else if (e.type === 'notPublishedMatch') dateMap[dateStr].notPublishedMatch++;
        else if (e.type === 'liked') dateMap[dateStr].liked++;
        else if (e.type === 'disliked') dateMap[dateStr].disliked++;
        else if (e.type === 'viewed') dateMap[dateStr].viewed++;
        else if (e.type === 'paid') {
          dateMap[dateStr].paid++;
          dateMap[dateStr].paidGraph += 10;
        }
        dateMap[dateStr].activity++;
      }
    });

    return Object.values(dateMap);
  }, [selectedUserDetails]);

  const conversionInsights = useMemo(() => {
    if (!selectedUserDetails) return null;
    const chatInsts = (selectedUserDetails.instructions || []).filter((i: any) => isChatInstruction(i));
    if (chatInsts.length === 0) return null;

    const firstChatTime = Math.min(...chatInsts.map((i: any) => new Date(i.date).getTime()));

    const postChatMatches = (selectedUserDetails.matches || []).filter((m: any) => new Date(m.created_at).getTime() > firstChatTime).length;
    const postChatPayments = (selectedUserDetails.transactions?.success_details || []).filter((t: any) => new Date(t.date).getTime() > firstChatTime).length;
    const totalPayments = selectedUserDetails.transactions?.summary?.success || 0;

    return {
      postChatMatches,
      postChatPayments,
      conversionRate: totalPayments > 0 ? ((postChatPayments / totalPayments) * 100).toFixed(0) : 0,
      isSuccess: postChatPayments > 0
    };
  }, [selectedUserDetails]);

  const likeDislikePieData = useMemo(() => {
    if (!selectedUserDetails?.likes) return [];
    const likes = selectedUserDetails.likes.filter((l: any) => l.liked).length;
    const dislikes = selectedUserDetails.likes.filter((l: any) => l.disliked).length;
    const viewed = selectedUserDetails.likes.filter((l: any) => !l.liked && !l.disliked).length;
    return [
      { name: 'Liked', value: likes, color: '#ec4899' },
      { name: 'Disliked', value: dislikes, color: '#ef4444' },
      { name: 'Viewed Only', value: viewed, color: '#64748b' }
    ].filter(i => i.value > 0);
  }, [selectedUserDetails]);

  useEffect(() => {
    fetchSupportAdmins();
  }, [fetchSupportAdmins]);

  useEffect(() => {
    if (activeMainTab === "unregistered") {
      fetchUnregisteredUsers(currentLimit, unregOffset);
    }
  }, [fetchUnregisteredUsers, activeMainTab, currentLimit, unregOffset]);

  useEffect(() => {
    if (activeMainTab === "unregistered") {
      fetchUnregStats("all");
    }
  }, [fetchUnregStats, activeMainTab]);

  useEffect(() => {
    if (selectedAdmin && activeMainTab === "performance") {
      fetchCallingDetails(selectedAdmin, selectedTimePeriod);
    }
  }, [selectedAdmin, selectedTimePeriod, fetchCallingDetails, activeMainTab]);

  const handleUpdateStatus = async (fromStatus: string, toStatus: string, userId: number | number[] | null = null) => {
    if (!userId && !showBulkConfirm) {
      setShowBulkConfirm(true);
      return;
    }
    setIsUpdatingStatus(true);
    const isArrayOfIds = Array.isArray(userId);
    const loadingToast = toast.loading(
      userId
        ? (isArrayOfIds ? `Updating ${userId.length} users to ${toStatus}...` : "Updating user status...")
        : `Bulk updating ${fromStatus} to ${toStatus}...`
    );
    try {
      const response = await PostBulkUpdateUnregisteredStatus(fromStatus, toStatus, userId as any);
      if (response.success || response.status === true || response.status === "success") {
        toast.success(response.message || "Status updated successfully", { id: loadingToast });
        fetchUnregisteredUsers();
        fetchUnregStats("all");
        setShowBulkConfirm(false);
      } else {
        toast.error(response.message || "Failed to update status", { id: loadingToast });
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred", { id: loadingToast });
    } finally {
      setIsUpdatingStatus(false);
    }
  };



  const filteredUnregisteredUsers = useMemo(() => {
    let users = [...unregisteredUsers];
    const normUnreg = unregFilterUser?.toLowerCase();
    const normFilterUser = (normUnreg === "soniya rathi" || normUnreg === "soniyarathi") ? "soniya" : normUnreg;
    if (normFilterUser) {
      users = users.filter(user => {
        const uAssigned = user.assigned_to?.toLowerCase();
        if (normFilterUser === "soniya") {
          return uAssigned === "soniya" || uAssigned === "soniya rathi" || uAssigned === "soniyarathi";
        }
        return uAssigned === normFilterUser;
      });
    }
    if (unregFilterStatus) users = users.filter(user => user.status?.toLowerCase() === unregFilterStatus.toLowerCase());
    if (unregFilterTag) users = users.filter(user => user.tag?.toLowerCase() === unregFilterTag.toLowerCase());

    if (unregFilterOnboarded === "onboarded") {
      users = users.filter(user => !!user.is_user_onboarded);
    } else if (unregFilterOnboarded === "pending") {
      users = users.filter(user => !user.is_user_onboarded);
    }

    // Sort by updated_at descending (newest first)
    users.sort((a: any, b: any) => {
      const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return dateB - dateA;
    });

    return users;
  }, [unregisteredUsers, unregFilterUser, unregFilterStatus, unregFilterTag, unregFilterOnboarded]);

  const unregOnboardedCount = useMemo(() => {
    return unregisteredUsers.filter((u: any) => !!u.is_user_onboarded).length;
  }, [unregisteredUsers]);

  const unregPendingCount = useMemo(() => {
    return unregisteredUsers.filter((u: any) => !u.is_user_onboarded).length;
  }, [unregisteredUsers]);

  const statsVisuals = useMemo(() => {
    if (!unregStats) return { pie: [], bar: [], tags: [] };
    const pie = [
      { name: 'Interested', value: unregStats.overall.interested, color: '#10b981' },
      { name: 'Not Interested', value: unregStats.overall.not_interested, color: '#64748b' },
      { name: 'Declined', value: unregStats.overall.declined, color: '#ef4444' },
      { name: 'Busy / Later', value: unregStats.overall.busy_call_later, color: '#f59e0b' },
      { name: 'Married', value: unregStats.overall.married_engaged, color: '#a855f7' },
      { name: 'Pending', value: unregStats.overall.pending, color: '#3b82f6' },
    ].filter(i => i.value > 0);
    const tags = Object.entries(unregStats.by_tag || {}).map(([name, value]) => ({ name, value }));
    return { pie, bar: tags, tags: Object.keys(unregStats.by_tag || {}) };
  }, [unregStats]);

  const handleExportToExcel = async () => {
    if (filteredUnregisteredUsers.length === 0) {
      toast.error("No data to export");
      return;
    }
    const loadingToast = toast.loading("Generating Excel file...");
    try {
      const XLSX = await import('xlsx');

      const excelData = filteredUnregisteredUsers.map((user: any) => ({
        "Lead ID": user.id || "",
        "Lead Name": user.name || "Anonymous Lead",
        "Contact Number": user.mobile_no || "",
        "City": user.city || "N/A",
        "State": user.state || "N/A",
        "Assigned Agent": user.assigned_to || "UNASSIGNED",
        "Current Status": user.status || "Pending",
        "Onboarding Status": user.is_user_onboarded ? "Onboarded" : "Pending",
        "Last Activity Date": user.updated_at ? new Date(user.updated_at).toLocaleString() : "N/A",
        "Feedback/Notes": user.feedback || "No feedback"
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Unregistered Leads");

      // Auto-fit columns to make it a "good excel"
      const max_widths = Object.keys(excelData[0]).map((key) => {
        let max_len = key.length;
        excelData.forEach((row: any) => {
          const val = row[key];
          const val_str = val ? val.toString() : "";
          if (val_str.length > max_len) {
            max_len = val_str.length;
          }
        });
        return { wch: Math.min(Math.max(max_len + 4, 12), 40) };
      });
      worksheet['!cols'] = max_widths;

      const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
      const dataBlob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });

      const fileUrl = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = fileUrl;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `Unregistered_Leads_${dateStr}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);

      toast.success("Excel exported successfully", { id: loadingToast });
    } catch (error) {
      console.error("Failed to export Excel:", error);
      toast.error("Failed to export Excel", { id: loadingToast });
    }
  };

  const handleExportStoreDataToExcel = async () => {
    if (storeData.length === 0) {
      toast.error("No data to export");
      return;
    }
    const loadingToast = toast.loading("Generating Excel file...");
    try {
      const XLSX = await import('xlsx');

      const excelData = storeData.map((data: any) => ({
        "User": data.name || "Anonymous",
        "Mobile No.": data.mobile_no || "",
        "Location": data.location || "N/A",
        "Submitted At": data.created_at ? new Date(data.created_at).toLocaleString() : "N/A"
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Web Unregistered Users");

      const max_widths = Object.keys(excelData[0]).map((key) => {
        let max_len = key.length;
        excelData.forEach((row: any) => {
          const val = row[key];
          const val_str = val ? val.toString() : "";
          if (val_str.length > max_len) {
            max_len = val_str.length;
          }
        });
        return { wch: Math.min(Math.max(max_len + 4, 12), 40) };
      });
      worksheet['!cols'] = max_widths;

      const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
      const dataBlob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });

      const fileUrl = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = fileUrl;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `Web_Unregistered_Users_${dateStr}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);

      toast.success("Excel exported successfully", { id: loadingToast });
    } catch (error) {
      console.error("Failed to export Excel:", error);
      toast.error("Failed to export Excel", { id: loadingToast });
    }
  };

  const metrics = useMemo(() => {
    if (!callingDetails?.users) return { total: 0, interested: 0, busy: 0, declined: 0 };
    const users = callingDetails.users;
    return {
      total: users.length,
      interested: users.filter((u: any) => u.status === "Interested").length,
      busy: users.filter((u: any) => u.status === "Busy Call Later").length,
      declined: users.filter((u: any) => u.status === "Declined" || u.status === "Not Interested").length,
    };
  }, [callingDetails]);

  const { perfBarData, perfPieData } = useMemo(() => {
    if (!callingDetails?.users) return { perfBarData: [], perfPieData: [] };
    const users = callingDetails.users;
    const days: { [key: string]: any } = {};
    users.forEach((user: any) => {
      const date = new Date(user.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!days[date]) days[date] = { name: date, Interested: 0, "Busy / Later": 0, Declined: 0 };
      if (user.status === "Interested") days[date].Interested++;
      else if (user.status === "Busy Call Later") days[date]["Busy / Later"]++;
      else days[date].Declined++;
    });
    const pie = [
      { name: 'Interested', value: metrics.interested, color: '#10b981' },
      { name: 'Busy / Later', value: metrics.busy, color: '#f59e0b' },
      { name: 'Declined', value: metrics.declined, color: '#ef4444' },
      { name: 'Pending', value: users.filter((u: any) => u.status === "pending").length, color: '#3b82f6' },
    ].filter(item => item.value > 0);
    return { perfBarData: Object.values(days).reverse(), perfPieData: pie };
  }, [callingDetails, metrics]);

  const formatFullDateTime = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return dateStr; }
  };

  const renderCustomizedPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (<text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-[11px] font-black uppercase tracking-tighter">{`${name} ${(percent * 100).toFixed(0)}%`}</text>);
  };

  return (
    <>
      <div className="min-h-screen bg-transparent p-3 md:p-6 space-y-6 animate-fade-in font-sans selection:bg-blue-500/30">
        {/* HEADER & TABS */}
        <div className="flex flex-col gap-6 border-b border-slate-800 pb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(59,130,246,0.6)]"></div>
                <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-blue-500/80">Support Analytics • Intelligence Hub</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-black text-white tracking-tight uppercase">Sales Performance Dashboard</h1>
              <p className="text-slate-400 font-medium text-sm max-w-2xl">Audit agent performance, track connection metrics, and monitor conversion success.</p>
            </div>
          </div>

          {/* RESPONSIVE TAB NAVIGATION */}
          <div className="w-full">
            <div className="flex flex-wrap gap-2 p-2 bg-slate-900/50 border border-slate-800 rounded-xl">
              <button
                onClick={() => setActiveMainTab("performance")}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 whitespace-nowrap ${activeMainTab === "performance" ? "bg-blue-600 text-white shadow-lg" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
              >
                <Activity size={12} /> Team Performance
              </button>
              <button
                onClick={() => setActiveMainTab("unregistered")}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 whitespace-nowrap ${activeMainTab === "unregistered" ? "bg-blue-600 text-white shadow-lg" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
              >
                <Users size={12} /> Unregistered Leads
              </button>
              <button
                onClick={() => setActiveMainTab("active-users")}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 whitespace-nowrap ${activeMainTab === "active-users" ? "bg-blue-600 text-white shadow-lg" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
              >
                <BarChart3 size={12} /> Active Insights
              </button>
              <button
                onClick={() => setActiveMainTab("unassigned-active-users")}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 whitespace-nowrap ${activeMainTab === "unassigned-active-users" ? "bg-blue-600 text-white shadow-lg" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
              >
                <UserMinus size={12} /> Unassigned
              </button>
              <button
                onClick={() => setActiveMainTab("hold-users")}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 whitespace-nowrap ${activeMainTab === "hold-users" ? "bg-blue-600 text-white shadow-lg" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
              >
                <Clock size={12} /> Hold
              </button>
              <button
                onClick={() => setActiveMainTab("not-interested")}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 whitespace-nowrap ${activeMainTab === "not-interested" ? "bg-blue-600 text-white shadow-lg" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
              >
                <UserX size={12} /> Not Interested
              </button>
              <button
                onClick={() => setActiveMainTab("store-data")}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 whitespace-nowrap ${activeMainTab === "store-data" ? "bg-blue-600 text-white shadow-lg" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
              >
                <Tag size={12} /> Web Unregisted Users
              </button>
            </div>
          </div>
        </div>

        {activeMainTab === "performance" ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative">
                <h2 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-6 flex items-center gap-2">
                  <Users size={10} className="text-blue-500" /> Active Support Agents
                </h2>
                <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar">
                  {loadingSupport ? (
                    <div className="py-16 flex justify-center">
                      <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                    </div>
                  ) : supportAdmins.length > 0 ? (
                    supportAdmins.map((admin, idx) => (
                      <button
                        key={admin.id || idx}
                        onClick={() => setSelectedAdmin(admin.username)}
                        className={`w-full p-3 rounded-xl border transition-all flex items-center justify-between group ${selectedAdmin === admin.username ? "bg-blue-600 border-blue-500 shadow-lg" : "bg-slate-950 border-slate-800 hover:border-blue-500/30"}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-[12px] transition-colors ${selectedAdmin === admin.username ? "bg-white text-blue-600" : "bg-slate-900 text-slate-400 group-hover:text-blue-400"}`}>
                            {admin.username?.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="text-left">
                            <div className={`text-[13px] font-bold tracking-tight ${selectedAdmin === admin.username ? "text-white" : "text-slate-200"}`}>
                              {admin.username}
                            </div>
                            <div className={`text-[9px] font-medium uppercase tracking-wide ${selectedAdmin === admin.username ? "text-blue-100" : "text-slate-600"}`}>
                              {admin.role || "Support Agent"}
                            </div>
                          </div>
                        </div>
                        <div className={`w-1.5 h-1.5 rounded-full ${selectedAdmin === admin.username ? "bg-white" : "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"}`} />
                      </button>
                    ))
                  ) : (
                    <div className="text-center py-16 opacity-50 flex flex-col items-center gap-3">
                      <Users size={32} className="text-slate-800" />
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600">No agents found</p>
                      <button onClick={fetchSupportAdmins} className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg text-[10px] font-bold uppercase tracking-wide text-blue-400 hover:text-blue-300 transition-all">
                        Retry
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                  <div className="space-y-1">
                    <h3 className="text-lg font-bold text-white uppercase tracking-tight">Performance Metrics</h3>
                    <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">
                      {selectedAdmin ? `Auditing: ${selectedAdmin}` : "Select an agent to begin audit"}
                    </p>
                  </div>
                  <div className="flex items-center p-1 bg-slate-950 border border-slate-800 rounded-xl">
                    {[{ id: "current", label: "Today" }, { id: "yesterday", label: "Yesterday" }, { id: "last_7_days", label: "7D" }, { id: "last_30_days", label: "30D" }, { id: "all", label: "All" }].map((period) => (
                      <button
                        key={period.id}
                        onClick={() => setSelectedTimePeriod(period.id)}
                        className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all ${selectedTimePeriod === period.id ? "bg-blue-600 text-white shadow-md" : "text-slate-500 hover:text-slate-300"}`}
                      >
                        {period.label}
                      </button>
                    ))}
                  </div>
                </div>
                {loadingCallingDetails ? (
                  <div className="py-16 flex flex-col items-center gap-4">
                    <div className="relative">
                      <div className="w-12 h-12 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Zap size={16} className="text-blue-500 animate-pulse" />
                      </div>
                    </div>
                    <span className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Analyzing Dataset...</span>
                  </div>
                ) : callingDetails && callingDetails.users && callingDetails.users.length > 0 ? (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 flex items-center justify-between group hover:border-blue-500/20 transition-all">
                        <div>
                          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Outreach</div>
                          <div className="text-3xl font-bold text-white tracking-tight">{metrics.total}</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-slate-700 group-hover:text-blue-500 transition-colors">
                          <Phone size={20} />
                        </div>
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 flex items-center justify-between group hover:border-emerald-500/20 transition-all">
                        <div>
                          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Interested</div>
                          <div className="text-3xl font-bold text-emerald-400 tracking-tight">{metrics.interested}</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-slate-700 group-hover:text-emerald-500 transition-colors">
                          <CheckCircle2 size={20} />
                        </div>
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 flex items-center justify-between group hover:border-amber-500/20 transition-all">
                        <div>
                          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Busy / Later</div>
                          <div className="text-3xl font-bold text-amber-400 tracking-tight">{metrics.busy}</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-slate-700 group-hover:text-amber-500 transition-colors">
                          <Clock size={20} />
                        </div>
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 flex items-center justify-between group hover:border-red-500/20 transition-all">
                        <div>
                          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Declined</div>
                          <div className="text-3xl font-bold text-red-400 tracking-tight">{metrics.declined}</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-slate-700 group-hover:text-red-500 transition-colors">
                          <XCircle size={20} />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <h4 className="text-[13px] font-bold text-white uppercase tracking-wide">Outcome Distribution</h4>
                            <p className="text-[9px] text-slate-600 font-medium uppercase tracking-wide">Agent conversion mix</p>
                          </div>
                          <PieChartIcon size={14} className="text-emerald-500" />
                        </div>
                        <div className="h-[200px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={perfPieData} innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value" label={renderCustomizedPieLabel} labelLine={false}>
                                {perfPieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} stroke="none" />))}
                              </Pie>
                              <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '0.75rem', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase' }} itemStyle={{ color: '#f8fafc' }} />
                              <Legend wrapperStyle={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', paddingTop: '8px' }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <h4 className="text-[13px] font-bold text-white uppercase tracking-wide">Outreach Velocity</h4>
                            <p className="text-[9px] text-slate-600 font-medium uppercase tracking-wide">Daily connection trend</p>
                          </div>
                          <TrendingUp size={14} className="text-blue-500" />
                        </div>
                        <div className="h-[200px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={perfBarData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                              <XAxis dataKey="name" stroke="#475569" fontSize={8} tickLine={false} axisLine={false} tick={{ fontWeight: 700 }} />
                              <YAxis stroke="#475569" fontSize={8} tickLine={false} axisLine={false} tick={{ fontWeight: 700 }} />
                              <Tooltip cursor={{ fill: 'rgba(51, 65, 85, 0.2)' }} contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '0.75rem', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase' }} itemStyle={{ color: '#f8fafc' }} />
                              <Bar dataKey="Interested" fill="#10b981" radius={[3, 3, 0, 0]} barSize={10} />
                              <Bar dataKey="Busy / Later" fill="#f59e0b" radius={[3, 3, 0, 0]} barSize={10} />
                              <Bar dataKey="Declined" fill="#ef4444" radius={[3, 3, 0, 0]} barSize={10} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : callingDetails ? (
                  <div className="py-16 flex flex-col items-center justify-center text-center opacity-70">
                    <div className="w-12 h-12 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-center text-slate-600 mb-4 shadow-inner">
                      <AlertCircle size={24} />
                    </div>
                    <h4 className="text-[15px] font-bold text-white uppercase tracking-wide mb-2">No Call Logs Detected</h4>
                    <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wide leading-relaxed max-w-md">
                      For this user ({selectedAdmin}) in this period ({selectedTimePeriod === "current" ? "Today" : selectedTimePeriod === "yesterday" ? "Yesterday" : selectedTimePeriod === "last_7_days" ? "7D" : selectedTimePeriod === "last_30_days" ? "30D" : "All"}) no call logs found
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center py-16 opacity-10">
                    <TrendingUp size={80} />
                    <p className="text-lg font-bold uppercase tracking-wide mt-6">Initialize Audit</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeMainTab === "active-users" ? (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* GLOBAL CONTROL BAR */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-slate-900 border border-slate-800 rounded-[2rem] p-8 shadow-2xl">
              <div className="space-y-1">
                <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">Active User Insight</h3>
                <p className="text-[12px] text-slate-500 font-bold uppercase tracking-[0.2em]">Customer journey tracking and self-assignment intelligence</p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {/* ASSIGNMENT TIME FILTER */}
                <div className="relative group min-w-[200px] dropdown-container">
                  <button
                    onClick={() => toggleDropdown("activeTime")}
                    className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Clock size={12} className={activeUserTimeFilter !== "all" ? "text-blue-500" : "text-slate-600"} />
                      <span className="text-[12px] font-black text-white uppercase tracking-widest">
                        {activeUserTimeFilter === "all" ? "All Assignments" :
                          activeUserTimeFilter === "today" ? "Assigned Today" :
                            activeUserTimeFilter === "yesterday" ? "Assigned Yesterday" :
                              activeUserTimeFilter === "weekly" ? "Weekly (7D)" :
                                activeUserTimeFilter === "half_monthly" ? "Last 15 Days (15D)" :
                                  activeUserTimeFilter === "monthly" ? "Monthly (30D)" :
                                    activeUserTimeFilter === "quarterly" ? "Quarterly (90D)" :
                                      "Yearly (365D)"}
                      </span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isActiveTimeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isActiveTimeDropdownOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                        <button onClick={() => { setActiveUserTimeFilter("all"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "all" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>All Assignments</button>
                        <button onClick={() => { setActiveUserTimeFilter("today"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "today" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Assigned Today</button>
                        <button onClick={() => { setActiveUserTimeFilter("yesterday"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "yesterday" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Assigned Yesterday</button>
                        <button onClick={() => { setActiveUserTimeFilter("weekly"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "weekly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Weekly (7D)</button>
                        <button onClick={() => { setActiveUserTimeFilter("half_monthly"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "half_monthly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Last 15 Days (15D)</button>
                        <button onClick={() => { setActiveUserTimeFilter("monthly"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "monthly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Monthly (30D)</button>
                        <button onClick={() => { setActiveUserTimeFilter("quarterly"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "quarterly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Quarterly (90D)</button>
                        <button onClick={() => { setActiveUserTimeFilter("yearly"); setIsActiveTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserTimeFilter === "yearly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Yearly (365D)</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* STATUS FILTER */}
                <div className="relative group min-w-[200px] dropdown-container">
                  <button
                    onClick={() => toggleDropdown("activeStatus")}
                    className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <ListFilter size={12} className={activeUserStatusFilter !== "all" ? "text-blue-500" : "text-slate-600"} />
                      <span className="text-[12px] font-black text-white uppercase tracking-widest">
                        {activeUserStatusFilter === "all" ? "All Active Nodes" :
                          activeUserStatusFilter === "paid" ? "Paid Users" :
                            activeUserStatusFilter === "failed" ? "Failed Attempts" :
                              "Pending Actions"}
                      </span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isActiveStatusDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isActiveStatusDropdownOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                        <button onClick={() => { setActiveUserStatusFilter("all"); setIsActiveStatusDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserStatusFilter === "all" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>All Active Nodes</button>
                        <button onClick={() => { setActiveUserStatusFilter("paid"); setIsActiveStatusDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserStatusFilter === "paid" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Paid Users</button>
                        <button onClick={() => { setActiveUserStatusFilter("failed"); setIsActiveStatusDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserStatusFilter === "failed" ? 'text-red-500 bg-red-500/5' : 'text-slate-400'}`}>Failed Attempts</button>
                        <button onClick={() => { setActiveUserStatusFilter("pending"); setIsActiveStatusDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${activeUserStatusFilter === "pending" ? 'text-amber-500 bg-amber-500/5' : 'text-slate-400'}`}>Pending Actions</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* PREMIUM STATUS FILTER */}
                <div className="relative group min-w-[200px] dropdown-container">
                  <button
                    onClick={() => toggleDropdown("premium")}
                    className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles size={12} className={premiumFilter !== "all" ? "text-amber-500 animate-pulse" : "text-slate-600"} />
                      <span className="text-[12px] font-black text-white uppercase tracking-widest">
                        {premiumFilter === "all" ? "All Premium Types" :
                          premiumFilter === "premium" ? "Premium Only" :
                            "Non-Premium Only"}
                      </span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isPremiumDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isPremiumDropdownOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                        <button onClick={() => { setPremiumFilter("all"); setIsPremiumDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${premiumFilter === "all" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>All Premium Types</button>
                        <button onClick={() => { setPremiumFilter("premium"); setIsPremiumDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${premiumFilter === "premium" ? 'text-amber-500 bg-amber-500/5' : 'text-slate-400'}`}>Premium Only</button>
                        <button onClick={() => { setPremiumFilter("non-premium"); setIsPremiumDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${premiumFilter === "non-premium" ? 'text-slate-500 bg-slate-500/5' : 'text-slate-400'}`}>Non-Premium Only</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* SPECIAL PREFERENCE FILTER */}
                <div className="relative group min-w-[200px] dropdown-container">
                  <button
                    onClick={() => toggleDropdown("specialPref")}
                    className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Tag size={12} className={specialPrefFilter !== "all" ? "text-blue-500 animate-pulse" : "text-slate-600"} />
                      <span className="text-[12px] font-black text-white uppercase tracking-widest">
                        {specialPrefFilter === "all" ? "All Preferences" :
                          specialPrefFilter === "special" ? "Special Preference Only" :
                            "No Special Preference"}
                      </span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isSpecialPrefDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isSpecialPrefDropdownOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                        <button onClick={() => { setSpecialPrefFilter("all"); setIsSpecialPrefDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${specialPrefFilter === "all" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>All Preferences</button>
                        <button onClick={() => { setSpecialPrefFilter("special"); setIsSpecialPrefDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${specialPrefFilter === "special" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Special Preference Only</button>
                        <button onClick={() => { setSpecialPrefFilter("no-special"); setIsSpecialPrefDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${specialPrefFilter === "no-special" ? 'text-slate-500 bg-slate-500/5' : 'text-slate-400'}`}>No Special Preference</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* TALKED STATUS FILTER */}
                <div className="relative group min-w-[200px] dropdown-container">
                  <button
                    onClick={() => toggleDropdown("interaction")}
                    className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <MessageSquare size={12} className={interactionFilter !== "all" ? "text-indigo-500 animate-pulse" : "text-slate-600"} />
                      <span className="text-[12px] font-black text-white uppercase tracking-widest">
                        {interactionFilter === "all" ? "All Chat States" :
                          interactionFilter === "talked" ? "Talked Only" :
                            "Not Talked Only"}
                      </span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isInteractionDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isInteractionDropdownOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                        <button onClick={() => { setInteractionFilter("all"); setIsInteractionDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${interactionFilter === "all" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>All Chat States</button>
                        <button onClick={() => { setInteractionFilter("talked"); setIsInteractionDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${interactionFilter === "talked" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Talked Only</button>
                        <button onClick={() => { setInteractionFilter("not-talked"); setIsInteractionDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${interactionFilter === "not-talked" ? 'text-rose-500 bg-rose-500/5' : 'text-slate-400'}`}>Not Talked Only</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* PAYMENT TIME FILTER */}
                <div className="relative group min-w-[200px] dropdown-container">
                  <button
                    onClick={() => toggleDropdown("paymentTime")}
                    className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <CreditCard size={12} className={paymentTimeFilter !== "all" ? "text-blue-500" : "text-slate-600"} />
                      <span className="text-[12px] font-black text-white uppercase tracking-widest">
                        {paymentTimeFilter === "all" ? "All Payments" :
                          paymentTimeFilter === "today" ? "Paid Today" :
                            paymentTimeFilter === "yesterday" ? "Paid Yesterday" :
                              paymentTimeFilter === "weekly" ? "Paid Last 7 Days" :
                                paymentTimeFilter === "half_monthly" ? "Paid Last 15 Days" :
                                  "Paid Last 30 Days"}
                      </span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isPaymentTimeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isPaymentTimeDropdownOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                        <button onClick={() => { setPaymentTimeFilter("all"); setIsPaymentTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${paymentTimeFilter === "all" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>All Payments</button>
                        <button onClick={() => { setPaymentTimeFilter("today"); setIsPaymentTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${paymentTimeFilter === "today" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Paid Today</button>
                        <button onClick={() => { setPaymentTimeFilter("yesterday"); setIsPaymentTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${paymentTimeFilter === "yesterday" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Paid Yesterday</button>
                        <button onClick={() => { setPaymentTimeFilter("weekly"); setIsPaymentTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${paymentTimeFilter === "weekly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Paid Last 7 Days</button>
                        <button onClick={() => { setPaymentTimeFilter("half_monthly"); setIsPaymentTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${paymentTimeFilter === "half_monthly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Paid Last 15 Days</button>
                        <button onClick={() => { setPaymentTimeFilter("monthly"); setIsPaymentTimeDropdownOpen(false); }} className={`w-full px-6 py-3 text-left text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${paymentTimeFilter === "monthly" ? 'text-blue-500 bg-blue-500/5' : 'text-slate-400'}`}>Paid Last 30 Days</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* SUPPORT SPECIALIST FILTER */}
                <div className="relative group min-w-[200px] dropdown-container">
                  <button
                    onClick={() => toggleDropdown("activeAdmin")}
                    className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl transition-all text-left shadow-lg hover:border-blue-500/30"
                  >
                    <div className="flex items-center gap-3">
                      <UserCheck size={12} className={selectedActiveAdmin ? "text-blue-500" : "text-slate-600"} />
                      <span className="text-[12px] font-black text-white uppercase tracking-widest">{selectedActiveAdmin || "All Support Specialists"}</span>
                    </div>
                    <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isActiveAdminDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isActiveAdminDropdownOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                        <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                          {isAdmin && (
                            <button onClick={() => { setSelectedActiveAdmin(""); setIsActiveAdminDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${selectedActiveAdmin === "" ? 'text-blue-500' : 'text-slate-400'}`}>All Support Specialists</button>
                          )}
                          {sortedSupportAdmins.map(admin => (
                            <button key={admin.id} onClick={() => { setSelectedActiveAdmin(admin.username); setIsActiveAdminDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${selectedActiveAdmin === admin.username ? 'text-blue-500' : 'text-slate-400'}`}>{admin.username}</button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* REFRESH DATASET BUTTON */}
                <button onClick={handleRefreshActiveUsers} className="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 hover:text-blue-400 hover:border-blue-500/30 transition-all shadow-lg flex items-center gap-2">
                  <RefreshCw size={16} className={loadingActiveUsers ? "animate-spin" : ""} />
                  <span className="text-[10px] font-black uppercase tracking-widest">Refresh Dataset</span>
                </button>
              </div>
            </div>

            {/* ACTIVE USERS SUMMARY STATS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group lg:col-span-2">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><Users size={80} /></div>
                <div className="relative z-10">
                  <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Active Users</span>
                  <div className="text-4xl font-black text-white tracking-tighter italic mt-1 flex items-baseline gap-1">
                    {loadingActiveUsers ? "..." : activeUsersSummary.total}
                    <span className="text-[13px] font-bold text-slate-500 tracking-normal not-italic ml-1">/ {totalActiveUsersCount}</span>
                  </div>
                  <div className="text-[9px] font-black uppercase mt-4 flex items-center gap-1.5 flex-wrap tracking-wider">
                    <span className="text-slate-600">Active: {periodLabelText}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-blue-400">UNIQUE USERS TALKED TO: {loadingActiveUsers ? "..." : activeUsersSummary.interacted}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-rose-400">NOT TALKED TO: {loadingActiveUsers ? "..." : activeUsersSummary.notInteracted}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-blue-400">TOTAL MESSAGES SENT: {loadingActiveUsers ? "..." : activeUsersSummary.chats}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-blue-400">AWAITING FOLLOW-UP: {loadingActiveUsers ? "..." : activeUsersSummary.awaitingFollowup}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-amber-400">PREMIUM: {loadingActiveUsers ? "..." : activeUsersSummary.premium}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-slate-400">NON-PREMIUM: {loadingActiveUsers ? "..." : activeUsersSummary.nonPremium}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-blue-400">SPECIAL PREF: {loadingActiveUsers ? "..." : activeUsersSummary.hasSpecialPref}</span>
                    <span className="text-slate-800">|</span>
                    <span className="text-slate-500">NO SPECIAL PREF: {loadingActiveUsers ? "..." : activeUsersSummary.noSpecialPref}</span>
                    <span className="text-slate-800">|</span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><CheckCircle2 size={80} /></div>
                <div className="relative z-10">
                  <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Recent Success</span>
                  <div className="text-4xl font-black text-emerald-400 tracking-tighter italic mt-1">{loadingActiveUsers ? "..." : activeUsersSummary.success}</div>
                  <p className="text-[10px] font-black text-slate-600 uppercase mt-4">Success: {paymentPeriodLabelText}</p>
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><XCircle size={80} /></div>
                <div className="relative z-10">
                  <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Recent Failed</span>
                  <div className="text-4xl font-black text-red-400 tracking-tighter italic mt-1">{loadingActiveUsers ? "..." : activeUsersSummary.failed}</div>
                  <p className="text-[10px] font-black text-slate-600 uppercase mt-4">Failed: {paymentPeriodLabelText}</p>
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><Clock size={80} /></div>
                <div className="relative z-10">
                  <span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Recent Pending</span>
                  <div className="text-4xl font-black text-amber-400 tracking-tighter italic mt-1">{loadingActiveUsers ? "..." : activeUsersSummary.pending}</div>
                  <p className="text-[10px] font-black text-slate-600 uppercase mt-4">Pending: {paymentPeriodLabelText}</p>
                </div>
              </div>
            </div>



            {/* VISUAL ANALYTICS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-8">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h3 className="text-[14px] font-black text-white uppercase tracking-[0.2em]">Support Reach Analysis</h3>
                    <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Active users with team interaction history</p>
                  </div>
                  <UserCheck size={18} className="text-indigo-500" />
                </div>
                <div className="h-[300px] w-full">
                  {loadingActiveUsers ? (
                    <div className="h-full flex items-center justify-center opacity-20"><Activity size={40} className="animate-spin" /></div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                      <PieChart>
                        <Pie data={aggregateSupportData.coverage} innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                          {aggregateSupportData.coverage.map((entry: any, index: number) => (<Cell key={`cell-${index}`} fill={entry.color} stroke="none" />))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '1rem', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase' }}
                          itemStyle={{ color: '#f8fafc' }}
                        />
                        <Legend wrapperStyle={{ fontSize: '9px', fontWeight: '900', textTransform: 'uppercase', paddingTop: '20px', color: '#94a3b8' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-8">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h3 className="text-[14px] font-black text-white uppercase tracking-[0.2em]">Team Transaction Trend</h3>
                    <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Daily unique users talked to vs. successful transactions</p>
                  </div>
                  <TrendingUp size={18} className="text-emerald-500" />
                </div>
                <div className="h-[300px] w-full">
                  {loadingActiveUsers ? (
                    <div className="h-full flex items-center justify-center opacity-20"><Activity size={40} className="animate-spin" /></div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                      <AreaChart data={aggregateSupportData.charts}>
                        <defs>
                          <linearGradient id="mainChatColor" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="mainConvColor" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} opacity={0.5} />
                        <XAxis dataKey="name" stroke="#475569" fontSize={8} tickLine={false} axisLine={false} tick={{ fontWeight: 900 }} />
                        <YAxis stroke="#475569" fontSize={8} tickLine={false} axisLine={false} tick={{ fontWeight: 900 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '1.25rem', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', padding: '12px' }}
                          itemStyle={{ color: '#f8fafc' }}
                          cursor={{ stroke: '#334155', strokeWidth: 1 }}
                        />
                        <Legend wrapperStyle={{ fontSize: '9px', fontWeight: '900', textTransform: 'uppercase', paddingTop: '15px', color: '#94a3b8' }} />
                        <Area type="monotone" name="Unique Users Talked To" dataKey="chats" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#mainChatColor)" />
                        <Area type="monotone" name="Successful Transactions" dataKey="conversions" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#mainConvColor)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            {/* DETAILED USER LIST BY AGENT */}
            <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10">
                <div className="space-y-1">
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">Active User Portfolio</h3>
                  <p className="text-[12px] text-slate-500 font-bold uppercase tracking-[0.2em]">Accounts grouped by assigned support specialist</p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  {dismissedAgents.size > 0 && (
                    <button
                      onClick={() => setDismissedAgents(new Set())}
                      className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-blue-400 uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2"
                    >
                      <RefreshCw size={12} /> Restore {dismissedAgents.size} Agents
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-10">
                {loadingActiveUsers ? (
                  <div className="py-32 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-10 h-10 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin" />
                      <span className="text-[12px] font-black text-slate-700 uppercase tracking-[0.4em]">Aggregating Intelligence...</span>
                    </div>
                  </div>
                ) : filteredActiveUsers.length > 0 ? (
                  filteredActiveUsers.map((group: any, idx: number) => {
                    const todayMidnight = new Date();
                    todayMidnight.setHours(0, 0, 0, 0);
                    const todayAssignedCount = group.users.filter((u: any) => {
                      const assignDate = getAssignmentDate({ ...u, assigned_to: group.assigned_to });
                      return assignDate && assignDate.getTime() >= todayMidnight.getTime();
                    }).length;

                    const totalAssignedCount = group.users.length;
                    const interactedCount = group.users.filter((u: any) => {
                      const chatInsts = (u.instructions || []).filter((inst: any) => isChatInstruction(inst) && isDateInPeriod(inst.date, activeUserTimeFilter));
                      return chatInsts.length > 0;
                    }).length;
                    const notInteractedCount = group.users.filter((u: any) => {
                      const chatInsts = (u.instructions || []).filter((inst: any) => isChatInstruction(inst) && isDateInPeriod(inst.date, activeUserTimeFilter));
                      return chatInsts.length === 0;
                    }).length;
                    const totalChatsCount = group.users.reduce((sum: number, u: any) => {
                      const chatInsts = (u.instructions || []).filter((inst: any) => isChatInstruction(inst) && isDateInPeriod(inst.date, activeUserTimeFilter));
                      return sum + chatInsts.length;
                    }, 0);
                    const awaitingFollowupCount = group.users.filter((u: any) => u.awaiting_followup && Array.isArray(u.awaiting_followup) && u.awaiting_followup.length > 0).length;

                    return (
                      <div key={idx} className="space-y-6">
                        <div className="flex items-center gap-4 group/header">
                          <div className="h-px flex-1 bg-slate-800" />
                          <div className="px-6 py-2 bg-slate-950 border border-slate-800 rounded-full flex items-center gap-4">
                            <span className="text-[11px] font-black text-blue-400 uppercase tracking-[0.3em]">
                              {group.assigned_to || "Unassigned"} (Assigned: {totalAssignedCount} | Talked To: {interactedCount} | Not Talked To: {notInteractedCount} | Total Messages Sent: {totalChatsCount} | Awaiting Follow-up: {awaitingFollowupCount} | Today: {todayAssignedCount})
                            </span>
                            <div className="flex items-center gap-2">
                              {dismissedUserIds.size > 0 && (
                                <button
                                  onClick={() => setDismissedUserIds(new Set())}
                                  className="text-[9px] font-black text-slate-500 hover:text-blue-400 uppercase tracking-widest transition-colors flex items-center gap-1"
                                >
                                  <RefreshCw size={10} /> Restore Users
                                </button>
                              )}
                              <button
                                onClick={() => setDismissedAgents(prev => new Set(prev).add(group.assigned_to || "Unassigned"))}
                                className="p-1 text-slate-600 hover:text-red-400 transition-colors"
                                title="Dismiss Agent Group"
                              >
                                <XCircle size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="h-px flex-1 bg-slate-800" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                          {group.users.slice(0, expandedAgents.has(group.assigned_to || "Unassigned") ? group.users.length : 10).map((user: any) => {
                            const actualInsts = (user.instructions || []).filter((inst: any) => {
                              if (isSystemMessage(inst)) return false;
                              const txt = (inst.instruction || "").toLowerCase();
                              if (txt.includes("this user is assigned to") || txt.includes("assigned to")) return false;
                              return isDateInPeriod(inst.date, activeUserTimeFilter);
                            });

                            const uniqueDates = new Set();
                            actualInsts.forEach((inst: any) => {
                              if (inst.date) {
                                const d = new Date(inst.date);
                                if (!isNaN(d.getTime())) {
                                  uniqueDates.add(d.toISOString().split('T')[0]);
                                } else {
                                  uniqueDates.add(inst.date);
                                }
                              } else {
                                uniqueDates.add(inst.id || Math.random().toString());
                              }
                            });
                            const datewiseCount = uniqueDates.size;
                            const latestInst = actualInsts.length > 0 ? actualInsts[0] : null;

                            return (
                              <div
                                key={user.user_id}
                                onClick={() => setSelectedUserDetails(user)}
                                className="bg-slate-950/50 border border-slate-800/50 rounded-3xl p-6 hover:border-blue-500/30 transition-all group relative overflow-hidden cursor-pointer active:scale-[0.98]"
                              >
                                {/* Close Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDismissedUserIds(prev => {
                                      const next = new Set(prev);
                                      next.add(user.user_id);
                                      return next;
                                    });
                                  }}
                                  className="absolute top-4 right-4 p-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-500 hover:text-red-400 hover:border-red-500/30 transition-all opacity-0 group-hover:opacity-100 z-20"
                                  title="Dismiss User"
                                >
                                  <XCircle size={14} />
                                </button>

                                <div className="flex items-start justify-between mb-6">
                                  <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-[18px] font-black text-slate-500 group-hover:text-blue-400 transition-colors">
                                      {user.username?.substring(0, 1).toUpperCase() || 'U'}
                                    </div>
                                    <div>
                                      <h4 className="text-[14px] font-black text-white uppercase tracking-tight group-hover:text-blue-400 transition-colors">{user.username}</h4>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="text-[10px] font-mono text-slate-600 uppercase">UID: {user.user_id}</span>
                                        {user.is_verified && <Shield size={10} className="text-emerald-500" />}
                                      </div>
                                      {user.phone && <div className="text-[10px] font-bold text-slate-500 mt-1 flex items-center gap-1"><Phone size={8} /> {user.phone}</div>}

                                      {latestInst && (
                                        <div className="mt-3 pt-2.5 border-t border-slate-800/35 max-w-[220px]">
                                          <div className="flex items-center justify-between gap-2 mb-1">
                                            <span className="text-[11px] font-black text-emerald-500 uppercase tracking-widest">Total Calls ({datewiseCount})</span>
                                            <span className="text-[8px] font-black text-slate-700 uppercase tracking-widest">{new Date(latestInst.date).toLocaleDateString()}</span>
                                          </div>
                                          <p className="text-[11px] text-slate-400 italic leading-relaxed break-words">
                                            "{latestInst.instruction}"
                                          </p>
                                          <div className="flex items-center gap-2 mt-1.5">
                                            <div className="px-1.5 py-0.5 bg-slate-900 rounded text-[8px] font-black text-slate-500 uppercase tracking-widest border border-slate-800">
                                              {latestInst.assigned_to || "System"}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-2">
                                    <div className={`px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-widest ${(user.transactions?.summary?.success || 0) > 0 ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-800 text-slate-500"
                                      }`}>
                                      {(user.transactions?.summary?.success || 0) > 0 ? "Paid User" : "No Success Tx"}
                                    </div>
                                    {activeUserStatusFilter === "paid" && user.transactions?.success_details?.some((tx: any) => {
                                      if ((tx.credits || 0) < 25) return false;
                                      const txTime = new Date(tx.date).getTime();
                                      const now = new Date();
                                      const diffDays = (now.getTime() - txTime) / (1000 * 60 * 60 * 24);
                                      return diffDays <= 90;
                                    }) && (
                                        <div className="px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-widest bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-yellow-500/30 text-yellow-400 flex items-center gap-1 shadow-md shadow-yellow-500/5 animate-pulse">
                                          <Sparkles size={8} className="text-yellow-400 animate-pulse" />
                                          Premium User
                                        </div>
                                      )}
                                    {user.special_preference_notes_exists && (
                                      <div className="px-2 py-1 rounded-md text-[8px] font-black uppercase tracking-widest bg-blue-500/10 border border-blue-500/30 text-blue-400 flex items-center gap-1 shadow-md shadow-blue-500/5">
                                        <Sparkles size={8} className="text-blue-400 animate-pulse" />
                                        Special Pref Note
                                      </div>
                                    )}
                                    {user.gender && (
                                      <span className="text-[8px] font-black text-slate-600 uppercase tracking-widest border border-slate-800 px-1.5 py-0.5 rounded-md">{user.gender}</span>
                                    )}
                                    {user.awaiting_followup && Array.isArray(user.awaiting_followup) && user.awaiting_followup.length > 0 && (
                                      <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[8px] font-black uppercase tracking-widest border border-blue-500/20 rounded-md">
                                        Follow-up Active
                                      </span>
                                    )}
                                    {(() => {
                                      const chatCount = (user.instructions || []).filter((inst: any) => isChatInstruction(inst) && isDateInPeriod(inst.date, activeUserTimeFilter)).length;
                                      if (chatCount > 0) {
                                        return (
                                          <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[8px] font-black uppercase tracking-widest border border-emerald-500/20 rounded-md">
                                            Chatting Active
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 mb-4">
                                  <div className="bg-slate-900/50 rounded-xl p-3 text-center">
                                    <div className="text-[14px] font-black text-emerald-400">{user.transactions?.summary?.success || 0}</div>
                                    <div className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">Success</div>
                                  </div>
                                  <div className="bg-slate-900/50 rounded-xl p-3 text-center">
                                    <div className="text-[14px] font-black text-red-400">{user.transactions?.summary?.failed || 0}</div>
                                    <div className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">Failed</div>
                                  </div>
                                  <div className="bg-slate-900/50 rounded-xl p-3 text-center">
                                    <div className="text-[14px] font-black text-amber-400">{user.transactions?.summary?.pending || 0}</div>
                                    <div className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">Pending</div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 mb-6">
                                  <div className="bg-slate-900/30 rounded-xl p-2.5 flex items-center justify-between px-3 border border-slate-800/30">
                                    <div className="flex items-center gap-1.5">
                                      <Heart size={10} className="text-pink-500" />
                                      <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Interests</span>
                                    </div>
                                    <div className="text-[12px] font-black text-white">{user.likes?.length || 0}</div>
                                  </div>
                                  <div className="bg-slate-900/30 rounded-xl p-2.5 flex items-center justify-between px-3 border border-slate-800/30">
                                    <div className="flex items-center gap-1.5">
                                      <Sparkles size={10} className="text-blue-400" />
                                      <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Matches</span>
                                    </div>
                                    <div className="text-[12px] font-black text-white">{user.matches?.length || 0}</div>
                                  </div>
                                  <div className="bg-slate-900/30 rounded-xl p-2.5 flex items-center justify-between px-3 border border-slate-800/30">
                                    <div className="flex items-center gap-1.5">
                                      <MessageSquare size={10} className="text-emerald-500" />
                                      <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">Total Messages Sent</span>
                                    </div>
                                    <div className="text-[12px] font-black text-white">
                                      {(user.instructions || []).filter((inst: any) => isChatInstruction(inst) && isDateInPeriod(inst.date, activeUserTimeFilter)).length}
                                    </div>
                                  </div>
                                </div>

                                {user.awaiting_followup && Array.isArray(user.awaiting_followup) && user.awaiting_followup.length > 0 && (
                                  <div className="space-y-4">
                                    <div className="pt-3 border-t border-slate-800/50">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                                          <span className="text-[9px] font-black text-blue-500 uppercase tracking-widest">Next Followup</span>
                                        </div>
                                        <span className="text-[10px] font-black text-white uppercase italic tracking-tighter">
                                          {user.awaiting_followup[0].followup_date}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {group.users.length > 10 && (
                          <div className="flex justify-center pt-4">
                            <button
                              onClick={() => {
                                setExpandedAgents(prev => {
                                  const next = new Set(prev);
                                  const key = group.assigned_to || "Unassigned";
                                  if (next.has(key)) next.delete(key);
                                  else next.add(key);
                                  return next;
                                });
                              }}
                              className="group/btn relative px-8 py-3 bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all shadow-xl"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000" />
                              <div className="relative flex items-center gap-3">
                                <span className="text-[11px] font-black text-slate-400 group-hover/btn:text-blue-400 uppercase tracking-[0.2em] transition-colors">
                                  {expandedAgents.has(group.assigned_to || "Unassigned") ? "Collapse Portfolio" : `View ${group.users.length - 10} More Users`}
                                </span>
                                <ChevronDown size={14} className={`text-slate-600 group-hover/btn:text-blue-500 transition-all ${expandedAgents.has(group.assigned_to || "Unassigned") ? "rotate-180" : ""}`} />
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="py-32 text-center opacity-10">
                    <Activity size={80} className="mx-auto mb-6" />
                    <p className="text-xl font-black uppercase tracking-[0.5em]">Intelligence Feed Empty</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeMainTab === "unassigned-active-users" ? (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* SUMMARY INFO */}
            <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10">
                <div className="space-y-1">
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">
                    Unassigned Completed Users
                  </h3>
                  <p className="text-[12px] text-slate-500 font-bold uppercase tracking-[0.2em]">
                    Manage and self-assign active profiles with 100% completion progress
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
                  <div className="relative flex items-center w-full sm:w-72">
                    <Search size={14} className="pointer-events-none absolute left-4 text-slate-500" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={completedSearchQuery}
                      onChange={(e) => {
                        setCompletedSearchQuery(e.target.value.toLowerCase());
                        setCompletedOffset(0);
                      }}
                      className="w-full pl-10 pr-10 py-3 bg-slate-950/80 border border-slate-800 focus:border-blue-500/50 rounded-2xl text-[10px] font-black uppercase tracking-wider text-white placeholder-slate-600 outline-none transition-all shadow-inner"
                    />
                    {completedSearchQuery && (
                      <button
                        onClick={() => {
                          setCompletedSearchQuery("");
                          setCompletedOffset(0);
                        }}
                        className="absolute right-4 text-slate-500 hover:text-white transition-colors"
                      >
                        <XCircle size={12} />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => fetchCompletedUsers(completedLimit, completedOffset, debouncedCompletedSearch)}
                    disabled={loadingCompletedUsers}
                    className="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 hover:text-blue-400 hover:border-blue-500/30 transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <RefreshCw size={16} className={loadingCompletedUsers ? "animate-spin" : ""} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Refresh</span>
                  </button>
                </div>
              </div>

              {/* TABLE CONTAINER */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-inner">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/40">
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">User Profile</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Demographics</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Location</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Contact Info</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Progress</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/40">
                      {loadingCompletedUsers ? (
                        <tr>
                          <td colSpan={6} className="py-20 text-center">
                            <div className="flex flex-col items-center justify-center gap-4">
                              <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                              <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Fetching completed profiles...</p>
                            </div>
                          </td>
                        </tr>
                      ) : filteredCompletedUsers.length > 0 ? (
                        filteredCompletedUsers.map((user: any) => {
                          const name = user.profile_details?.name || user.username || "Unknown User";
                          const city = user.profile_details?.city || "N/A";
                          const state = user.profile_details?.state || "N/A";
                          const caste = user.profile_details?.caste || "N/A";
                          const dob = user.profile_details?.date_of_birth || "N/A";
                          const gender = user.gender || user.profile_details?.gender || "N/A";

                          return (
                            <tr key={user.user_id} className="hover:bg-slate-900/10 transition-colors group">
                              {/* USER PROFILE */}
                              <td className="px-6 py-5">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center font-black text-slate-400 group-hover:border-blue-500/30 transition-all uppercase text-[12px]">
                                    {name.substring(0, 2)}
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[13px] font-black text-white group-hover:text-blue-400 transition-colors">{name}</span>
                                      {user.is_verified && (
                                        <div className="px-1.5 py-0.5 bg-blue-500/10 rounded-md text-[8px] font-black text-blue-400 uppercase tracking-widest border border-blue-500/20">
                                          Verified
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase">
                                      <span>ID: {user.user_id}</span>
                                      <span>•</span>
                                      <span className={gender.toLowerCase() === "female" ? "text-pink-400" : gender.toLowerCase() === "male" ? "text-blue-400" : ""}>{gender}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* DEMOGRAPHICS */}
                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <div className="text-[12px] font-black text-slate-300 uppercase tracking-tight">Caste: {caste}</div>
                                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">DOB: {dob}</div>
                                </div>
                              </td>

                              {/* LOCATION */}
                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <div className="text-[12px] font-black text-slate-300 uppercase tracking-tight">{city}</div>
                                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{state}</div>
                                </div>
                              </td>

                              {/* CONTACT INFO */}
                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5 text-[12px] font-black text-slate-300">
                                    <Phone size={10} className="text-slate-500" />
                                    <span>{user.phone_number || "N/A"}</span>
                                  </div>
                                  {user.email_address && (
                                    <div className="text-[10px] font-bold text-slate-500 lowercase truncate max-w-[150px]">
                                      {user.email_address}
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* PROGRESS */}
                              <td className="px-6 py-5 text-center">
                                <div className="inline-flex flex-col items-center gap-1">
                                  <div className="px-2.5 py-1 bg-emerald-500/10 rounded-full text-[10px] font-black text-emerald-400 uppercase tracking-widest border border-emerald-500/20">
                                    {user.progress}% Done
                                  </div>
                                  <span className="text-[9px] font-bold text-slate-600 uppercase tracking-wider">
                                    {formatFullDateTime(user.created_at)}
                                  </span>
                                </div>
                              </td>

                              {/* ACTIONS */}
                              <td className="px-6 py-5 text-right">
                                <button
                                  onClick={() => handleAssignToSelf(user.user_id)}
                                  disabled={isAssigning !== null}
                                  className="px-5 py-2.5 bg-blue-600/10 hover:bg-blue-600 hover:text-white border border-blue-500/20 hover:border-blue-500 text-blue-400 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md hover:shadow-blue-500/10 disabled:opacity-50"
                                >
                                  {isAssigning === user.user_id ? "Assigning..." : "Assign to Self"}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-24 text-center">
                            <div className="flex flex-col items-center justify-center opacity-30 gap-4">
                              <UserMinus size={48} className="text-slate-500" />
                              <p className="text-sm font-black uppercase tracking-[0.2em] text-slate-400">No Unassigned Completed Users Found</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION FOOTER */}
                {completedTotal > 0 && (
                  <div className="px-6 py-5 border-t border-slate-800 bg-slate-900/20 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Show:</span>
                      <select
                        value={completedLimit}
                        onChange={(e) => {
                          const newLimit = parseInt(e.target.value);
                          setCompletedLimit(newLimit);
                          setCompletedOffset(0);
                          fetchCompletedUsers(newLimit, 0);
                        }}
                        className="bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-[10px] font-black text-white outline-none focus:border-blue-500/50"
                      >
                        {[10, 20, 50, 100].map(val => (
                          <option key={val} value={val}>{val} items</option>
                        ))}
                      </select>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-2">
                        Showing {completedOffset + 1} - {Math.min(completedOffset + completedLimit, completedTotal)} of {completedTotal}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const newOffset = Math.max(0, completedOffset - completedLimit);
                          setCompletedOffset(newOffset);
                          fetchCompletedUsers(completedLimit, newOffset);
                        }}
                        disabled={completedOffset === 0 || loadingCompletedUsers}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newOffset = completedOffset + completedLimit;
                          setCompletedOffset(newOffset);
                          fetchCompletedUsers(completedLimit, newOffset);
                        }}
                        disabled={completedOffset + completedLimit >= completedTotal || loadingCompletedUsers}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeMainTab === "hold-users" ? (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10">
                <div className="space-y-1">
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">
                    Hold Users <span className="text-blue-500 not-italic font-bold">({holdUsersTotal})</span>
                  </h3>
                  <p className="text-[12px] text-slate-500 font-bold uppercase tracking-[0.2em]">
                    Review profiles currently paused by support with hold duration details
                  </p>
                </div>
                <button
                  onClick={() => fetchHoldUsers(holdUsersLimit, holdUsersOffset)}
                  disabled={loadingHoldUsers}
                  className="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 hover:text-blue-400 hover:border-blue-500/30 transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <RefreshCw size={16} className={loadingHoldUsers ? "animate-spin" : ""} />
                  <span className="text-[10px] font-black uppercase tracking-widest">Refresh</span>
                </button>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-inner">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/40">
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">User Profile</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Demographics</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Location</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Contact</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Hold Period</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Hold Reason</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Assigned To</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/40">
                      {loadingHoldUsers ? (
                        <tr>
                          <td colSpan={8} className="py-20 text-center">
                            <div className="flex flex-col items-center justify-center gap-4">
                              <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                              <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Fetching hold users...</p>
                            </div>
                          </td>
                        </tr>
                      ) : holdUsers.length > 0 ? (
                        holdUsers.map((user: any) => {
                          const name = user.profile_details?.name || user.username || "Unknown User";
                          const gender = user.gender || user.profile_details?.gender || "N/A";
                          const caste = user.profile_details?.caste || "N/A";
                          const dob = user.profile_details?.date_of_birth || "N/A";
                          const city = user.profile_details?.city || "N/A";
                          const state = user.profile_details?.state || "N/A";
                          const startedAt = user.hold_started_at ? new Date(user.hold_started_at) : null;
                          const duration = Number(user.hold_duration_days || 0);
                          const endsAt = startedAt && duration > 0
                            ? new Date(startedAt.getTime() + duration * 24 * 60 * 60 * 1000)
                            : null;

                          return (
                            <tr key={user.user_id} className="hover:bg-slate-900/10 transition-colors group">
                              <td className="px-6 py-5">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center font-black text-blue-400 group-hover:border-blue-500/40 transition-all uppercase text-[12px]">
                                    {name.substring(0, 2)}
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[13px] font-black text-white group-hover:text-blue-400 transition-colors">{name}</span>
                                      {user.is_verified && (
                                        <div className="px-1.5 py-0.5 bg-blue-500/10 rounded-md text-[8px] font-black text-blue-400 uppercase tracking-widest border border-blue-500/20">
                                          Verified
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase">
                                      <span>ID: {user.user_id}</span>
                                      <span>-</span>
                                      <span className={gender.toLowerCase() === "female" ? "text-pink-400" : gender.toLowerCase() === "male" ? "text-blue-400" : ""}>{gender}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <div className="text-[12px] font-black text-slate-300 uppercase tracking-tight">Caste: {caste}</div>
                                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">DOB: {dob}</div>
                                  <div className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">{user.progress || 0}% complete</div>
                                </div>
                              </td>

                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <div className="text-[12px] font-black text-slate-300 uppercase tracking-tight">{city}</div>
                                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{state}</div>
                                </div>
                              </td>

                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5 text-[12px] font-black text-slate-300">
                                    <Phone size={10} className="text-slate-500" />
                                    <span>{user.phone_number || "N/A"}</span>
                                  </div>
                                  {user.email_address && (
                                    <div className="text-[10px] font-bold text-slate-500 lowercase truncate max-w-[170px]">
                                      {user.email_address}
                                    </div>
                                  )}
                                </div>
                              </td>

                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-blue-400">
                                    <Clock size={10} />
                                    {duration || "N/A"} Day{duration === 1 ? "" : "s"}
                                  </div>
                                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    Start: {startedAt ? formatFullDateTime(user.hold_started_at) : "N/A"}
                                  </div>
                                  <div className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                                    End: {endsAt ? formatFullDateTime(endsAt.toISOString()) : "N/A"}
                                  </div>
                                </div>
                              </td>

                              <td className="px-6 py-5">
                                {user.hold_reason ? (
                                  <div
                                    className="inline-flex items-center gap-1.5 rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-amber-400 max-w-[200px] break-words shadow-sm"
                                    title={user.hold_reason}
                                  >
                                    {user.hold_reason}
                                  </div>
                                ) : (
                                  <span className="text-[10px] font-bold text-slate-600">—</span>
                                )}
                              </td>

                              <td className="px-6 py-5">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 flex items-center justify-center font-black text-[12px] uppercase">
                                    {(user.assigned_to || "U").substring(0, 1)}
                                  </div>
                                  <span className="text-[12px] font-black text-white uppercase tracking-widest">
                                    {user.assigned_to || "Unassigned"}
                                  </span>
                                </div>
                              </td>

                              <td className="px-6 py-5">
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleUnholdUser(user.user_id)}
                                    disabled={loadingHoldUsers}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-emerald-400 transition-all hover:border-emerald-500/40 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    <RefreshCw size={12} />
                                    Unhold User
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="py-20 text-center opacity-10">
                            <Clock size={80} className="mx-auto mb-6" />
                            <p className="text-xl font-black uppercase tracking-[0.5em]">No Hold Users Found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {holdUsersTotal > 0 && (
                  <div className="px-6 py-5 border-t border-slate-800 bg-slate-900/20 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Show:</span>
                      <select
                        value={holdUsersLimit}
                        onChange={(e) => {
                          const newLimit = parseInt(e.target.value);
                          setHoldUsersLimit(newLimit);
                          setHoldUsersOffset(0);
                          fetchHoldUsers(newLimit, 0);
                        }}
                        className="bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-[10px] font-black text-white outline-none focus:border-blue-500/50 cursor-pointer"
                      >
                        {[10, 20, 50, 100].map(val => (
                          <option key={val} value={val}>{val} items</option>
                        ))}
                      </select>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-2">
                        {holdUsers.length > 0 ? (
                          <>Showing {holdUsersOffset + 1} - {holdUsersOffset + holdUsers.length} of {holdUsersTotal}</>
                        ) : (
                          "No items"
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const newOffset = Math.max(0, holdUsersOffset - holdUsersLimit);
                          setHoldUsersOffset(newOffset);
                          fetchHoldUsers(holdUsersLimit, newOffset);
                        }}
                        disabled={holdUsersOffset === 0 || loadingHoldUsers}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newOffset = holdUsersOffset + holdUsersLimit;
                          setHoldUsersOffset(newOffset);
                          fetchHoldUsers(holdUsersLimit, newOffset);
                        }}
                        disabled={holdUsersOffset + holdUsersLimit >= holdUsersTotal || loadingHoldUsers}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeMainTab === "not-interested" ? (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* SUMMARY INFO */}
            <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10">
                <div className="space-y-1">
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">
                    Not-Interested Users
                  </h3>
                  <p className="text-[12px] text-slate-500 font-bold uppercase tracking-[0.2em]">
                    Monitor profiles that have indicated they are not interested
                  </p>
                </div>
                <div className="flex items-center gap-4 w-full md:w-auto justify-end">
                  <button
                    onClick={() => fetchNotInterestedUsers(notInterestedLimit, notInterestedOffset)}
                    disabled={loadingNotInterested}
                    className="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 hover:text-blue-400 hover:border-blue-500/30 transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <RefreshCw size={16} className={loadingNotInterested ? "animate-spin" : ""} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Refresh</span>
                  </button>
                </div>
              </div>

              {/* TABLE CONTAINER */}
              <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-inner">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/40">
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">User Profile</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Gender</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Assigned Specialist</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Contact Number</th>
                        <th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/40">
                      {loadingNotInterested ? (
                        <tr>
                          <td colSpan={5} className="py-20 text-center">
                            <div className="flex flex-col items-center justify-center gap-4">
                              <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                              <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Fetching not-interested profiles...</p>
                            </div>
                          </td>
                        </tr>
                      ) : notInterestedUsers.length > 0 ? (
                        notInterestedUsers.map((user: any) => {
                          const name = user.name || user.username || user.userid || "Unknown User";
                          const gender = user.gender || "N/A";
                          const assignedTo = user.assignedto || "UNASSIGNED";
                          const mobile = user["mobile no."] || "N/A";
                          const lastActivity = formatFullDateTime(user.lastactivity);

                          return (
                            <tr key={user.userid} className="hover:bg-slate-900/10 transition-colors group">
                              {/* USER PROFILE */}
                              <td className="px-6 py-5">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center font-black text-slate-400 group-hover:border-blue-500/30 transition-all uppercase text-[12px]">
                                    {name.substring(0, 2)}
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[13px] font-black text-white group-hover:text-blue-400 transition-colors">{name}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase">
                                      <span>ID: {user.userid}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* GENDER */}
                              <td className="px-6 py-5">
                                <span className={`text-[12px] font-black uppercase tracking-widest ${gender.toLowerCase() === "female" ? "text-pink-400" : gender.toLowerCase() === "male" ? "text-blue-400" : "text-slate-400"}`}>{gender}</span>
                              </td>

                              {/* ASSIGNED SPECIALIST */}
                              <td className="px-6 py-5">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-xl bg-blue-600/10 border border-blue-600/20 text-blue-500 flex items-center justify-center font-black text-[12px] uppercase">
                                    {assignedTo.substring(0, 1)}
                                  </div>
                                  <span className="text-[12px] font-black text-white uppercase tracking-widest">{assignedTo}</span>
                                </div>
                              </td>

                              {/* CONTACT NUMBER */}
                              <td className="px-6 py-5">
                                <span className="text-[12px] font-bold text-slate-400 tracking-wider">{mobile}</span>
                              </td>

                              {/* LAST ACTIVITY */}
                              <td className="px-6 py-5 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                                {lastActivity}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="py-20 text-center opacity-10">
                            <UserX size={80} className="mx-auto mb-6" />
                            <p className="text-xl font-black uppercase tracking-[0.5em]">No Not-Interested Users Found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION FOOTER */}
                {notInterestedTotal > 0 && (
                  <div className="px-6 py-5 border-t border-slate-800 bg-slate-900/20 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Show:</span>
                      <select
                        value={notInterestedLimit}
                        onChange={(e) => {
                          const newLimit = parseInt(e.target.value);
                          setNotInterestedLimit(newLimit);
                          setNotInterestedOffset(0);
                          fetchNotInterestedUsers(newLimit, 0);
                        }}
                        className="bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-[10px] font-black text-white outline-none focus:border-blue-500/50 cursor-pointer"
                      >
                        {[10, 20, 50, 100].map(val => (
                          <option key={val} value={val}>{val} items</option>
                        ))}
                      </select>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-2">
                        {notInterestedUsers.length > 0 ? (
                          <>
                            Showing {notInterestedOffset + 1} - {notInterestedOffset + notInterestedUsers.length} of {notInterestedTotal}
                          </>
                        ) : (
                          "No items"
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const newOffset = Math.max(0, notInterestedOffset - notInterestedLimit);
                          setNotInterestedOffset(newOffset);
                          fetchNotInterestedUsers(notInterestedLimit, newOffset);
                        }}
                        disabled={notInterestedOffset === 0 || loadingNotInterested}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newOffset = notInterestedOffset + notInterestedLimit;
                          setNotInterestedOffset(newOffset);
                          fetchNotInterestedUsers(notInterestedLimit, newOffset);
                        }}
                        disabled={notInterestedOffset + notInterestedLimit >= notInterestedTotal || loadingNotInterested}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeMainTab === "store-data" ? (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10">
                <div className="space-y-1">
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">Web Unregister User<span className="text-blue-500 not-italic font-bold">({storeData.length})</span></h3>
                  <p className="text-[12px] text-slate-500 font-bold uppercase tracking-[0.2em]">Contact Submissions from Smart Matrimony</p>
                </div>
                <button
                  onClick={handleExportStoreDataToExcel}
                  title="Export to Excel"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[12px] font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-blue-900/20 transition-all"
                >
                  <Download size={14} />
                  <span>Export Excel</span>
                </button>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-slate-800">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-950/50">
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-800">User</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-800">Mobile No.</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-800">Location</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-800">Submitted At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/40">
                    {loadingStoreData ? (
                      <tr><td colSpan={4} className="py-20 text-center text-slate-500">Loading...</td></tr>
                    ) : storeData.length > 0 ? (
                      storeData.map((data: any) => (
                        <tr key={data.id} className="hover:bg-slate-900/10 transition-colors">
                          <td className="px-6 py-5 text-[13px] font-black text-white uppercase">{data.name}</td>
                          <td className="px-6 py-5 text-[12px] font-bold text-slate-400">{data.mobile_no}</td>
                          <td className="px-6 py-5 text-[12px] font-bold text-slate-400 uppercase">{data.location || "N/A"}</td>
                          <td className="px-6 py-5 text-[11px] font-black text-slate-500 uppercase">{data.created_at ? new Date(data.created_at).toLocaleString() : "N/A"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={4} className="py-20 text-center text-slate-500">No Store Data Found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* MACRO STATS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group"><div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><Users size={80} /></div><div className="relative z-10"><span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Total Users</span><div className="text-4xl font-black text-white tracking-tighter italic mt-1">{loadingUnregStats ? "..." : unregStats?.overall?.total || 0}</div><div className="mt-4 flex items-center gap-2"><div className="px-2 py-0.5 bg-emerald-500/10 rounded-md text-[10px] font-black text-emerald-500 uppercase tracking-widest">Master Pool</div></div></div></div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group"><div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><UserCheck size={80} /></div><div className="relative z-10"><span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Agent Assignment</span><div className="text-4xl font-black text-blue-400 tracking-tighter italic mt-1">{loadingUnregStats ? "..." : `${unregStats?.assignment?.coverage_percent || 0}%`}</div><div className="mt-4 flex items-center gap-2 text-[10px] font-black text-slate-600 uppercase tracking-widest"><span className="text-blue-500">{unregStats?.assignment?.assigned || 0} Assigned</span> / {unregStats?.assignment?.unassigned || 0} unassigned</div></div></div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group"><div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><Tag size={80} /></div><div className="relative z-10"><span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Most Active Tag</span><div className="text-xl font-black text-white uppercase tracking-tighter italic mt-1 truncate">{loadingUnregStats ? "..." : statsVisuals.tags.sort((a, b) => (unregStats.by_tag[b] || 0) - (unregStats.by_tag[a] || 0))[0] || 'None'}</div><div className="mt-4 flex items-center gap-2 text-[10px] font-black text-slate-600 uppercase tracking-widest">{statsVisuals.tags.length} Total Tags Found</div></div></div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-8 relative overflow-hidden group"><div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><PieChartIcon size={80} /></div><div className="relative z-10"><span className="text-[12px] font-black text-slate-500 uppercase tracking-[0.2em]">Pending Users</span><div className="text-4xl font-black text-amber-400 tracking-tighter italic mt-1">{loadingUnregStats ? "..." : unregStats?.overall?.pending || 0}</div><div className="mt-4 flex items-center gap-2 text-[10px] font-black text-slate-600 uppercase tracking-widest">Awaiting Initial Outreach</div></div></div>
            </div>

            {/* DETAILED STATS BREAKDOWN */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-8">
                <div className="flex items-center justify-between"><div className="space-y-1"><h3 className="text-[14px] font-black text-white uppercase tracking-[0.2em]">Overall Status Mix</h3><p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Global distribution across all statuses</p></div><div className="w-10 h-10 bg-slate-950 rounded-2xl flex items-center justify-center text-emerald-500 border border-slate-800"><Activity size={18} /></div></div>
                <div className="h-[300px] w-full"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={statsVisuals.pie} innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" label={renderCustomizedPieLabel} labelLine={false}>{statsVisuals.pie.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} stroke="none" />))}</Pie><Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '1rem', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase' }} itemStyle={{ color: '#f8fafc' }} /><Legend wrapperStyle={{ fontSize: '9px', fontWeight: '900', textTransform: 'uppercase', paddingTop: '20px' }} /></PieChart></ResponsiveContainer></div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-8">
                <div className="flex items-center justify-between"><div className="space-y-1"><h3 className="text-[14px] font-black text-white uppercase tracking-[0.2em]">Tag Distribution</h3><p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Lead volume categorized by source tags</p></div><div className="w-10 h-10 bg-slate-950 rounded-2xl flex items-center justify-center text-blue-500 border border-slate-800"><Tag size={18} /></div></div>
                <div className="h-[300px] w-full"><ResponsiveContainer width="100%" height="100%"><BarChart data={statsVisuals.bar} layout="vertical" margin={{ left: 40, right: 40 }}><CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} /><XAxis type="number" hide /><YAxis dataKey="name" type="category" stroke="#475569" fontSize={8} tickLine={false} axisLine={false} tick={{ fontWeight: 900 }} width={100} tickFormatter={(val) => val?.toUpperCase()} /><Tooltip cursor={{ fill: 'rgba(51, 65, 85, 0.2)' }} contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '1rem', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase' }} itemStyle={{ color: '#f8fafc' }} /><Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} label={{ position: 'right', fill: '#94a3b8', fontSize: 9, fontWeight: 900 }} /></BarChart></ResponsiveContainer></div>
              </div>
            </div>

            {/* BULK UPDATE TOOLBAR */}
            <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 shadow-2xl border-l-4 border-l-blue-500">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-500"><RefreshCw size={24} className={isUpdatingStatus ? "animate-spin" : ""} /></div><div><h3 className="text-[16px] font-black text-white uppercase tracking-widest">Bulk Status Update Controller</h3><p className="text-[12px] text-slate-500 font-bold uppercase tracking-widest mt-1">Here you can change the status of all Unregister User</p></div></div><div className="flex flex-wrap items-center gap-4"><div className="flex flex-col gap-1"><span className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">From Status</span><select value={bulkFromStatus} onChange={(e) => setBulkFromStatus(e.target.value)} className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-[12px] font-black text-white uppercase tracking-widest outline-none focus:border-blue-500/50 transition-all">{statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div><div className="h-4 w-px bg-slate-800 self-end mb-3 hidden lg:block" /><div className="flex flex-col gap-1"><span className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">To Status</span><select value={bulkToStatus} onChange={(e) => setBulkToStatus(e.target.value)} className="bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-[12px] font-black text-emerald-400 uppercase tracking-widest outline-none focus:border-emerald-500/50 transition-all">{statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div><div className="self-end mb-1">{showBulkConfirm ? (<div className="flex items-center gap-2 animate-in zoom-in-95 duration-200"><button onClick={() => handleUpdateStatus(bulkFromStatus, bulkToStatus)} className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-xl text-[12px] font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-emerald-900/20"><Check size={12} /> Confirm Global Update</button><button onClick={() => setShowBulkConfirm(false)} className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2.5 rounded-xl text-[12px] font-black uppercase tracking-widest flex items-center gap-2">Cancel</button></div>) : (<button onClick={() => setShowBulkConfirm(true)} disabled={isUpdatingStatus} className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-8 py-2.5 rounded-xl text-[12px] font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-blue-900/20 transition-all group">Execute Bulk Update <ChevronDown size={14} className="group-hover:translate-y-0.5 transition-transform" /></button>)}</div></div></div>
              {showBulkConfirm && (<div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-start gap-4 animate-in slide-in-from-top-2"><AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" /><div className="text-[12px] font-bold text-amber-200 uppercase leading-relaxed tracking-wider">Caution: This will update <span className="text-amber-500 font-black">ALL</span> unregistered leads currently in <span className="text-white font-black underline underline-offset-4">"{bulkFromStatus}"</span> status to <span className="text-white font-black underline underline-offset-4">"{bulkToStatus}"</span>. This action is direct and irreversible.</div></div>)}
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10"><div className="space-y-1"><h3 className="text-xl font-black text-white uppercase italic tracking-tighter">Unregistered Leads Pipeline <span className="text-blue-500 not-italic font-bold">({filteredUnregisteredUsers.length})</span></h3><p className="text-[12px] text-slate-500 font-bold uppercase tracking-[0.2em]">Direct access to pending registration dataset</p></div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="relative group min-w-[180px] dropdown-container"><button onClick={() => toggleDropdown("unregTag")} className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"><div className="flex items-center gap-3"><Tag size={12} className={unregFilterTag ? "text-blue-500" : "text-slate-600"} /><span className="text-[12px] font-black text-white uppercase tracking-widest truncate max-w-[100px]">{unregFilterTag || "All Tags"}</span></div><ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isTagDropdownOpen ? 'rotate-180' : ''}`} /></button><AnimatePresence>{isTagDropdownOpen && (<motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2"><button onClick={() => { setUnregFilterTag(""); setUnregOffset(0); setIsTagDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${!unregFilterTag ? 'text-blue-500' : 'text-slate-400'}`}>All Tags</button><div className="h-px bg-slate-900 my-1 mx-4" /><div className="max-h-[300px] overflow-y-auto custom-scrollbar">{statsVisuals.tags.map(tag => (<button key={tag} onClick={() => { setUnregFilterTag(tag); setUnregOffset(0); setIsTagDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${unregFilterTag === tag ? 'text-blue-500' : 'text-slate-400'}`}>{tag}</button>))}</div></motion.div>)}</AnimatePresence></div>
                  <div className="relative group min-w-[180px] dropdown-container">
                    <button
                      onClick={() => toggleDropdown("unregAgent")}
                      className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl transition-all text-left shadow-lg hover:border-blue-500/30"
                    >
                      <div className="flex items-center gap-3">
                        <Users size={12} className={unregFilterUser ? "text-blue-500" : "text-slate-600"} />
                        <span className="text-[12px] font-black text-white uppercase tracking-widest">{unregFilterUser || "All Agents"}</span>
                      </div>
                      <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isAgentDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {isAgentDropdownOpen && (
                        <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2">
                          <button onClick={() => { setUnregFilterUser(""); setUnregOffset(0); setIsAgentDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${!unregFilterUser ? 'text-blue-500' : 'text-slate-400'}`}>All Agents</button>
                          <div className="h-px bg-slate-900 my-1 mx-4" />
                          <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                            {supportAdmins.map(admin => (
                              <button key={admin.id} onClick={() => { setUnregFilterUser(admin.username); setUnregOffset(0); setIsAgentDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${unregFilterUser === admin.username ? 'text-blue-500' : 'text-slate-400'}`}>{admin.username}</button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="relative group min-w-[180px] dropdown-container"><button onClick={() => toggleDropdown("unregStatus")} className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"><div className="flex items-center gap-3"><ListFilter size={12} className={unregFilterStatus ? "text-emerald-500" : "text-slate-600"} /><span className="text-[12px] font-black text-white uppercase tracking-widest">{unregFilterStatus || "All Status"}</span></div><ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isStatusDropdownOpen ? 'rotate-180' : ''}`} /></button><AnimatePresence>{isStatusDropdownOpen && (<motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2"><button onClick={() => { setUnregFilterStatus(""); setUnregOffset(0); setIsStatusDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${!unregFilterStatus ? 'text-emerald-500' : 'text-slate-400'}`}>All Status</button><div className="h-px bg-slate-900 my-1 mx-4" />{statusOptions.map(option => (<button key={option} onClick={() => { setUnregFilterStatus(option); setUnregOffset(0); setIsStatusDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${unregFilterStatus === option ? 'text-emerald-500' : 'text-slate-400'}`}>{option}</button>))}</motion.div>)}</AnimatePresence></div>
                  <div className="relative group min-w-[180px] dropdown-container"><button onClick={() => toggleDropdown("unregOnboarded")} className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg"><div className="flex items-center gap-3"><CheckCircle2 size={12} className={unregFilterOnboarded !== "all" ? "text-emerald-500" : "text-slate-600"} /><span className="text-[12px] font-black text-white uppercase tracking-widest">{unregFilterOnboarded === "all" ? "All Onboarded" : unregFilterOnboarded === "onboarded" ? "Onboarded" : "Pending"}</span></div><ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isOnboardedDropdownOpen ? 'rotate-180' : ''}`} /></button><AnimatePresence>{isOnboardedDropdownOpen && (<motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 5, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2"><button onClick={() => { setUnregFilterOnboarded("all"); setUnregOffset(0); setIsOnboardedDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${unregFilterOnboarded === "all" ? 'text-emerald-500' : 'text-slate-400'}`}>All Onboarded</button><div className="h-px bg-slate-900 my-1 mx-4" /><button onClick={() => { setUnregFilterOnboarded("onboarded"); setUnregOffset(0); setIsOnboardedDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${unregFilterOnboarded === "onboarded" ? 'text-emerald-500' : 'text-slate-400'}`}>Onboarded</button><button onClick={() => { setUnregFilterOnboarded("pending"); setUnregOffset(0); setIsOnboardedDropdownOpen(false); }} className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${unregFilterOnboarded === "pending" ? 'text-emerald-500' : 'text-slate-400'}`}>Pending</button></motion.div>)}</AnimatePresence></div>
                  <div className="flex items-center gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl text-[12px] font-black uppercase tracking-widest shadow-lg">
                    <span className="text-blue-500">Total: {unregisteredUsers.length}</span>
                    <div className="h-3 w-px bg-slate-800" />
                    <span className="text-emerald-500">Onboarded: {unregOnboardedCount}</span>
                    <div className="h-3 w-px bg-slate-800" />
                    <span className="text-slate-400">Pending: {unregPendingCount}</span>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl min-w-[190px]">
                    <span className="text-[12px] font-black text-slate-500 uppercase tracking-widest">From:</span>
                    <input
                      type="date"
                      value={unregStartDate}
                      onChange={(e) => { setUnregStartDate(e.target.value); setUnregOffset(0); }}
                      style={{ colorScheme: 'dark' }}
                      className="bg-transparent border-none text-[12px] font-black text-white outline-none focus:ring-0 w-full text-left uppercase tracking-widest cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl min-w-[190px]">
                    <span className="text-[12px] font-black text-slate-500 uppercase tracking-widest">To:</span>
                    <input
                      type="date"
                      value={unregEndDate}
                      onChange={(e) => { setUnregEndDate(e.target.value); setUnregOffset(0); }}
                      style={{ colorScheme: 'dark' }}
                      className="bg-transparent border-none text-[12px] font-black text-white outline-none focus:ring-0 w-full text-left uppercase tracking-widest cursor-pointer"
                    />
                  </div>
                  <div className="relative group min-w-[140px] dropdown-container">
                    <button
                      disabled={!unregStartDate && !unregEndDate}
                      onClick={() => toggleDropdown("unregLimit")}
                      className="w-full flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 px-5 py-3 rounded-2xl hover:border-blue-500/30 transition-all text-left shadow-lg disabled:opacity-70 disabled:hover:border-slate-800"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-black text-slate-500 uppercase tracking-widest">Limit:</span>
                        <span className="text-[12px] font-black text-white uppercase tracking-widest">{currentLimit}</span>
                      </div>
                      {!!(unregStartDate || unregEndDate) && (
                        <ChevronDown size={14} className={`text-slate-600 transition-transform duration-300 ${isLimitDropdownOpen ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    <AnimatePresence>
                      {isLimitDropdownOpen && (unregStartDate || unregEndDate) && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 5, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className="absolute top-full left-0 w-full bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2 mt-2"
                        >
                          {[10, 20, 50, 100].map(val => (
                            <button
                              key={val}
                              onClick={() => {
                                setUnregLimit(val);
                                setUnregOffset(0);
                                setIsLimitDropdownOpen(false);
                                fetchUnregisteredUsers(val, 0);
                              }}
                              className={`w-full px-5 py-3 text-left text-[12px] font-black uppercase tracking-widest hover:bg-slate-900 transition-colors ${unregLimit === val ? 'text-blue-500' : 'text-slate-400'}`}
                            >
                              {val}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <button onClick={() => fetchUnregisteredUsers(currentLimit, unregOffset)} className="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 hover:text-blue-400 hover:border-blue-500/30 transition-all shadow-lg" title="Refresh Data"><Clock size={16} /></button>
                  <button
                    onClick={handleExportToExcel}
                    title="Export to Excel"
                    className="p-3.5 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 hover:text-emerald-400 hover:border-emerald-500/30 transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <Download size={16} />
                    <span className="text-[12px] font-black uppercase tracking-widest hidden sm:inline">Export</span>
                  </button>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-800/50 bg-slate-950/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-[12px] font-black text-slate-600 uppercase tracking-widest border-b border-slate-800/50">
                        <th className="px-10 py-6">Lead Identity</th>
                        <th className="px-10 py-6">Contact Info</th>
                        <th className="px-10 py-6">Current Status</th>
                        <th className="px-10 py-6">Onboarded</th>
                        <th className="px-10 py-6">Last Activity</th>
                        <th className="px-10 py-6">Feedback</th>
                        <th className="px-10 py-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Bulk:</span>
                            <select
                              value=""
                              onChange={(e) => {
                                const newStatus = e.target.value;
                                if (!newStatus) return;
                                const userIds = filteredUnregisteredUsers.map((u: any) => u.id);
                                if (userIds.length === 0) return;
                                if (window.confirm(`Update status of all ${userIds.length} users currently showing in the table to "${newStatus}"?`)) {
                                  handleUpdateStatus("", newStatus, userIds);
                                }
                                e.target.value = ""; // Reset
                              }}
                              disabled={isUpdatingStatus || filteredUnregisteredUsers.length === 0}
                              className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1 text-[11px] font-black text-blue-500 hover:text-blue-400 uppercase tracking-widest outline-none focus:border-blue-500/50 transition-all cursor-pointer hover:bg-slate-900"
                            >
                              <option value="">Actions ({filteredUnregisteredUsers.length})</option>
                              {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/30">
                      {unregLoading ? (
                        <tr>
                          <td colSpan={7} className="py-32 text-center">
                            <div className="flex flex-col items-center gap-4">
                              <div className="w-10 h-10 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin" />
                              <span className="text-[12px] font-black text-slate-700 uppercase tracking-[0.4em]">Fetching Lead Intelligence...</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredUnregisteredUsers.length > 0 ? (
                        filteredUnregisteredUsers.map((user: any) => (
                          <tr key={user.id} className="group hover:bg-slate-800/20 transition-all">
                            <td className="px-10 py-6">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-center text-[14px] font-black text-slate-600 group-hover:text-blue-400 transition-colors">
                                  {user.name?.substring(0, 1).toUpperCase() || 'U'}
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[13px] font-black text-white uppercase tracking-tight group-hover:text-blue-400 transition-colors">
                                    {user.name || 'Anonymous Lead'}
                                  </span>
                                  <div className="flex items-center gap-1 mt-1">
                                    <MapPin size={8} className="text-slate-600" />
                                    <span className="text-[10px] font-mono text-slate-600 uppercase">
                                      {user.city || 'Unknown City'}, {user.state || 'Unknown State'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-10 py-6">
                              <div className="flex flex-col">
                                <span className="text-[12px] font-bold text-slate-400 tracking-wider">{user.mobile_no}</span>
                                <span className="text-[10px] font-black text-slate-600 uppercase mt-1">UID: {user.id}</span>
                              </div>
                            </td>
                            <td className="px-10 py-6">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-[12px] font-black border transition-all ${user.status === 'Interested'
                                  ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
                                  : user.status === 'Busy Call Later'
                                    ? 'bg-amber-500/10 border-amber-500/20 text-amber-500'
                                    : 'bg-blue-600/10 border-blue-600/20 text-blue-500'
                                  }`}>
                                  {user.assigned_to?.substring(0, 1).toUpperCase() || 'N'}
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[11px] font-black text-white uppercase tracking-widest">{user.assigned_to || 'UNASSIGNED'}</span>
                                  <span className={`text-[7px] font-bold uppercase ${user.status === 'Interested'
                                    ? 'text-emerald-500'
                                    : user.status === 'Busy Call Later'
                                      ? 'text-amber-400'
                                      : 'text-slate-600'
                                    }`}>
                                    {user.status}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-10 py-6">
                              {user.is_user_onboarded ? (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-[10px] font-black text-emerald-400 uppercase tracking-widest">
                                  <CheckCircle2 size={12} className="text-emerald-400" />
                                  <span>Onboarded</span>
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-900 border border-slate-800 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest">
                                  <XCircle size={12} className="text-slate-500" />
                                  <span>Pending</span>
                                </div>
                              )}
                            </td>
                            <td className="px-10 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                              {formatFullDateTime(user.updated_at)}
                            </td>
                            <td className="px-10 py-6">
                              <p className="text-[12px] font-medium text-slate-500 italic max-w-[150px] truncate group-hover:text-slate-300 transition-colors">
                                "{user.feedback || 'No feedback'}"
                              </p>
                            </td>
                            <td className="px-10 py-6 text-right">
                              <select
                                value={user.status}
                                onChange={(e) => handleUpdateStatus(user.status, e.target.value, user.id)}
                                disabled={isUpdatingStatus}
                                className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-[11px] font-black text-white uppercase tracking-widest outline-none focus:border-blue-500/50 transition-all cursor-pointer hover:bg-slate-900"
                              >
                                {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="py-32 text-center opacity-10">
                            <Users size={80} className="mx-auto mb-6" />
                            <p className="text-xl font-black uppercase tracking-[0.5em]">No Records Found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION FOOTER */}
                {unregTotal > 0 && (
                  <div className="px-6 py-5 border-t border-slate-800 bg-slate-900/20 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Show:</span>
                      {(!unregStartDate && !unregEndDate) ? (
                        <div className="bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-[10px] font-black text-slate-400">
                          100 items
                        </div>
                      ) : (
                        <select
                          value={unregLimit}
                          onChange={(e) => {
                            const newLimit = parseInt(e.target.value);
                            setUnregLimit(newLimit);
                            setUnregOffset(0);
                            fetchUnregisteredUsers(newLimit, 0);
                          }}
                          className="bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-[10px] font-black text-white outline-none focus:border-blue-500/50 cursor-pointer"
                        >
                          {[10, 20, 50, 100].map(val => (
                            <option key={val} value={val}>{val} items</option>
                          ))}
                        </select>
                      )}
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-2">
                        {unregisteredUsers.length > 0 ? (
                          <>
                            Showing {unregOffset + 1} - {unregOffset + unregisteredUsers.length}
                            {unregHasTotal && ` of ${unregTotal}`}
                          </>
                        ) : (
                          "No items"
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const newOffset = Math.max(0, unregOffset - currentLimit);
                          setUnregOffset(newOffset);
                          fetchUnregisteredUsers(currentLimit, newOffset);
                        }}
                        disabled={unregOffset === 0 || unregLoading}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newOffset = unregOffset + currentLimit;
                          setUnregOffset(newOffset);
                          fetchUnregisteredUsers(currentLimit, newOffset);
                        }}
                        disabled={
                          unregHasTotal
                            ? unregOffset + currentLimit >= unregTotal || unregLoading
                            : unregisteredUsers.length < currentLimit || unregLoading
                        }
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-[10px] font-black text-white uppercase tracking-widest hover:border-slate-700 transition-all disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* USER DETAILS MODAL - Moved outside main container for better stacking context */}
      <AnimatePresence>
        {selectedUserDetails && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedUserDetails(null)}
              className="absolute inset-0 bg-slate-950/90 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              className="relative w-full max-w-5xl bg-slate-900 border border-slate-800 rounded-[3rem] shadow-[0_0_100px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col max-h-[85vh] z-[1000]"
            >
              {/* Modal Header */}
              <div className="p-10 border-b border-slate-800 flex items-start justify-between bg-slate-950/40">
                <div className="flex items-center gap-8">
                  <div className="w-20 h-20 rounded-[2rem] bg-blue-600 flex items-center justify-center text-3xl font-black text-white shadow-2xl shadow-blue-600/30">
                    {selectedUserDetails.username?.substring(0, 1).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div className="flex items-center gap-4 mb-2">
                      <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter">{selectedUserDetails.username || 'Anonymous User'}</h2>
                      <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border ${(selectedUserDetails.transactions?.summary?.success || 0) > 0
                        ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                        : "bg-slate-800 text-slate-500 border-slate-700"
                        }`}>
                        {(selectedUserDetails.transactions?.summary?.success || 0) > 0 ? "Premium Node" : "Standard Node"}
                      </div>
                      {selectedUserDetails.special_preference_notes_exists && (
                        <div className="px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border bg-blue-500/10 text-blue-400 border-blue-500/20 flex items-center gap-1.5 shadow-md">
                          <Sparkles size={10} className="text-blue-400 animate-pulse" />
                          Special Pref Note
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest">UID:</span>
                        <span className="text-[11px] font-mono font-bold text-slate-400">{selectedUserDetails.user_id || 'N/A'}</span>
                      </div>
                      <div className="w-1.5 h-1.5 bg-slate-800 rounded-full" />
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest">Last Activity:</span>
                        <span className="text-[11px] font-bold text-slate-400">
                          {selectedUserDetails.instructions_updated_at && isDateInPeriod(selectedUserDetails.instructions_updated_at, activeUserTimeFilter)
                            ? new Date(selectedUserDetails.instructions_updated_at).toLocaleDateString()
                            : (() => {
                              const actual = (selectedUserDetails.instructions || []).filter((inst: any) => !isSystemMessage(inst) && isDateInPeriod(inst.date, activeUserTimeFilter));
                              return actual.length > 0 ? new Date(actual[0].date).toLocaleDateString() : 'Never';
                            })()
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUserDetails(null)}
                  className="p-4 bg-slate-950 border border-slate-800 rounded-3xl text-slate-500 hover:text-white hover:border-slate-600 transition-all shadow-xl"
                >
                  <XCircle size={28} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-10 space-y-12 bg-slate-900">
                {/* Metrics Section */}
                <div className="space-y-6">
                  <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><Activity size={14} className="text-blue-500" /> Transaction Intelligence</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="bg-slate-950 border border-slate-800 rounded-3xl p-8 text-center relative overflow-hidden group">
                      <div className="relative z-10">
                        <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Total Load</div>
                        <div className="text-4xl font-black text-white italic tracking-tighter">{selectedUserDetails.transactions?.summary?.total || 0}</div>
                      </div>
                      <div className="absolute top-0 right-0 p-2 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity"><Zap size={60} /></div>
                    </div>
                    <div className="bg-slate-950 border border-emerald-500/20 rounded-3xl p-8 text-center border-b-4 border-b-emerald-500/50 shadow-xl shadow-emerald-500/5">
                      <div className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-2">Confirmed Success</div>
                      <div className="text-4xl font-black text-emerald-400 italic tracking-tighter">{selectedUserDetails.transactions?.summary?.success || 0}</div>
                    </div>
                    <div className="bg-slate-950 border border-red-500/20 rounded-3xl p-8 text-center border-b-4 border-b-red-500/50 shadow-xl shadow-red-500/5">
                      <div className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-2">Failed Attempts</div>
                      <div className="text-4xl font-black text-red-400 italic tracking-tighter">{selectedUserDetails.transactions?.summary?.failed || 0}</div>
                    </div>
                    <div className="bg-slate-950 border border-amber-500/20 rounded-3xl p-8 text-center border-b-4 border-b-amber-500/50 shadow-xl shadow-amber-500/5">
                      <div className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-2">Pending State</div>
                      <div className="text-4xl font-black text-amber-400 italic tracking-tighter">{selectedUserDetails.transactions?.summary?.pending || 0}</div>
                    </div>
                  </div>

                  {/* Special Preference Notes Section */}
                  {(selectedUserDetails.special_preference_notes_exists || selectedUserDetails.special_preference_notes) && (
                    <div className="space-y-6">
                      <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3">
                        <Sparkles size={14} className="text-blue-500 animate-pulse" />
                        Special Preference Notes
                      </h3>
                      <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] p-10 shadow-2xl space-y-8 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity">
                          <Tag size={120} />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
                          {/* Left: Structured List */}
                          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {(() => {
                              let notes = selectedUserDetails.special_preference_notes;
                              if (typeof notes === 'string') {
                                try {
                                  notes = JSON.parse(notes);
                                } catch (e) {
                                  // keep as is
                                }
                              }
                              if (!notes) {
                                return (
                                  <div className="md:col-span-2 text-slate-500 text-[11px] uppercase tracking-widest font-black py-4">
                                    No preference notes available.
                                  </div>
                                );
                              }

                              const renderNotesList = (title: string, obj: any, titleColor: string) => {
                                if (!obj || typeof obj !== 'object' || Object.keys(obj).length === 0) return null;
                                return (
                                  <div className="space-y-4">
                                    <h4 className={`text-[12px] font-black uppercase tracking-widest ${titleColor}`}>{title}</h4>
                                    <div className="space-y-3">
                                      {Object.entries(obj).map(([key, val]: [string, any]) => {
                                        const formattedVal = Array.isArray(val)
                                          ? val.join(", ")
                                          : typeof val === 'object'
                                            ? JSON.stringify(val)
                                            : String(val);
                                        if (!formattedVal) return null;
                                        return (
                                          <div key={key} className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4 flex flex-col gap-1.5 hover:border-slate-700/60 transition-all">
                                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{key.replace(/_/g, ' ')}</span>
                                            <span className="text-[13px] font-bold text-white uppercase tracking-tight">{formattedVal}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              };

                              const hasIncludes = notes.includes && Object.keys(notes.includes).length > 0;
                              const hasExcludes = notes.excludes && Object.keys(notes.excludes).length > 0;

                              if (hasIncludes || hasExcludes) {
                                return (
                                  <>
                                    {hasIncludes && renderNotesList("Inclusive Preferences", notes.includes, "text-emerald-400")}
                                    {hasExcludes && renderNotesList("Exclusive Preferences", notes.excludes, "text-rose-400")}
                                  </>
                                );
                              }

                              const flatNotes = Object.entries(notes).filter(([_, v]) => v);
                              if (flatNotes.length > 0) {
                                return (
                                  <div className="md:col-span-2 space-y-4">
                                    <h4 className="text-[12px] font-black uppercase tracking-widest text-slate-300">Preferences</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {flatNotes.map(([key, val]: [string, any]) => {
                                        const formattedVal = Array.isArray(val)
                                          ? val.join(", ")
                                          : typeof val === 'object'
                                            ? JSON.stringify(val)
                                            : String(val);
                                        return (
                                          <div key={key} className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-4 flex flex-col gap-1.5 hover:border-slate-700/60 transition-all">
                                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{key.replace(/_/g, ' ')}</span>
                                            <span className="text-[13px] font-bold text-white uppercase tracking-tight">{formattedVal}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="md:col-span-2 text-slate-500 text-[11px] uppercase tracking-widest font-black py-4">
                                  No structured preferences found.
                                </div>
                              );
                            })()}
                          </div>

                          {/* Right: Beautiful formatted Raw JSON view */}
                          <div className="space-y-4">
                            <h4 className="text-[12px] font-black uppercase tracking-widest text-blue-400">Raw JSON View</h4>
                            <div className="bg-slate-900 border border-slate-800/80 rounded-3xl p-6 font-mono text-[11px] leading-relaxed text-blue-300 overflow-x-auto max-h-[350px] custom-scrollbar shadow-inner relative group/json">
                              <pre className="whitespace-pre-wrap word-break-all select-all selection:bg-blue-500/20">
                                {(() => {
                                  let notes = selectedUserDetails.special_preference_notes;
                                  if (typeof notes === 'string') {
                                    try {
                                      notes = JSON.parse(notes);
                                    } catch (e) { }
                                  }
                                  return JSON.stringify(notes, null, 2);
                                })()}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Customer Journey Intelligence */}
                  <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] p-10 space-y-10 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-8 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity">
                      <TrendingUp size={120} />
                    </div>

                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 relative z-10">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                          <h3 className="text-[14px] font-black text-white uppercase tracking-[0.3em]">Customer Journey Intelligence</h3>
                        </div>
                        <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Attribution analysis of support interactions vs user conversion</p>
                      </div>

                      {conversionInsights && (
                        <div className="flex flex-wrap items-center gap-6">
                          <div className="bg-slate-900 border border-slate-800 rounded-2xl px-6 py-3">
                            <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Post-Chat Matches</div>
                            <div className="text-xl font-black text-blue-400 italic">+{conversionInsights.postChatMatches}</div>
                          </div>
                          <div className="bg-slate-900 border border-slate-800 rounded-2xl px-6 py-3">
                            <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Post-Chat Payments</div>
                            <div className="text-xl font-black text-emerald-400 italic">+{conversionInsights.postChatPayments}</div>
                          </div>
                          <div className={`rounded-2xl px-6 py-3 border ${conversionInsights.isSuccess ? "bg-emerald-500/10 border-emerald-500/20" : "bg-slate-900 border-slate-800"}`}>
                            <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Attribution Score</div>
                            <div className={`text-xl font-black italic ${conversionInsights.isSuccess ? "text-emerald-500" : "text-slate-400"}`}>{conversionInsights.conversionRate}%</div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="h-[300px] w-full relative z-10">
                      {userJourneyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={userJourneyData}>
                            <defs>
                              <linearGradient id="journeyGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} opacity={0.5} />
                            <XAxis
                              dataKey="name"
                              stroke="#475569"
                              fontSize={9}
                              tickLine={false}
                              axisLine={false}
                              tick={{ fontWeight: 900 }}
                            />
                            <YAxis hide />
                            <Tooltip
                              formatter={(value: any, name: any, props: any) => {
                                if (name === "Subscription Success") return [props.payload.paid, name];
                                if (name === "Total Engagement") return [props.payload.activity, name];
                                if (name === "Interests (Liked)") return [props.payload.liked, name];
                                if (name === "Disliked/Viewed") return [props.payload.disliked + props.payload.viewed, name];
                                return [value, name];
                              }}
                              contentStyle={{
                                backgroundColor: '#0f172a',
                                border: '1px solid #334155',
                                borderRadius: '1.25rem',
                                fontSize: '10px',
                                fontWeight: '900',
                                textTransform: 'uppercase',
                                padding: '12px'
                              }}
                              cursor={{ stroke: '#334155', strokeWidth: 1 }}
                            />
                            <Legend
                              wrapperStyle={{
                                fontSize: '9px',
                                fontWeight: '900',
                                textTransform: 'uppercase',
                                paddingTop: '20px'
                              }}
                            />
                            <Area
                              name="Total Engagement"
                              type="monotone"
                              dataKey="activity"
                              stroke="#3b82f6"
                              strokeWidth={2}
                              fillOpacity={1}
                              fill="url(#journeyGradient)"
                            />
                            <Bar
                              name="Support Chats"
                              dataKey="chat"
                              fill="#6366f1"
                              radius={[4, 4, 0, 0]}
                              barSize={15}
                            />
                            <Bar
                              name="Published Matches"
                              dataKey="publishedMatch"
                              stackId="matches"
                              fill="#10b981"
                              radius={[0, 0, 0, 0]}
                              barSize={15}
                            />
                            <Bar
                              name="Draft Matches"
                              dataKey="notPublishedMatch"
                              stackId="matches"
                              fill="#f59e0b"
                              radius={[4, 4, 0, 0]}
                              barSize={15}
                            />
                            <Line
                              name="Interests (Liked)"
                              type="monotone"
                              dataKey="liked"
                              stroke="#ec4899"
                              strokeWidth={2}
                              dot={{ fill: '#ec4899', r: 3 }}
                            />
                            <Line
                              name="Disliked/Viewed"
                              type="monotone"
                              dataKey="disliked"
                              stroke="#ef4444"
                              strokeWidth={2}
                              dot={{ fill: '#ef4444', r: 3 }}
                            />
                            <Line
                              name="Subscription Success"
                              type="monotone"
                              dataKey="paidGraph"
                              stroke="#10b981"
                              strokeWidth={3}
                              dot={{ fill: '#10b981', r: 5, strokeWidth: 2, stroke: '#0f172a' }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center opacity-20">
                          <Activity size={48} className="mb-4" />
                          <span className="text-[12px] font-black uppercase tracking-[0.3em]">Insufficient Journey Data</span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-800/50 relative z-10">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                          <MessageSquare size={20} />
                        </div>
                        <div>
                          <div className="text-[11px] font-black text-white uppercase tracking-widest mb-1">Interaction Strategy</div>
                          <p className="text-[10px] text-slate-500 leading-relaxed font-medium">Analyze how chat frequency correlates with the "Subscription Success" line on the graph.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
                          <Zap size={20} />
                        </div>
                        <div>
                          <div className="text-[11px] font-black text-white uppercase tracking-widest mb-1">Conversion Velocity</div>
                          <p className="text-[10px] text-slate-500 leading-relaxed font-medium">A successful conversion is measured by a green dot appearing after a purple "Support Chat" bar.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Interaction History Section */}
                <div className="space-y-6">
                  <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><MessageSquare size={14} className="text-emerald-500" /> Interaction History Logs</h3>
                  <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
                    {(() => {
                      const actualInsts = (selectedUserDetails.instructions || []).filter((item: any) =>
                        !isSystemMessage(item) && isDateInPeriod(item.date, activeUserTimeFilter)
                      );
                      return actualInsts.length > 0 ? (
                        <div className="divide-y divide-slate-800/50">
                          {[...actualInsts].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((item: any, idx: number) => (
                            <div key={idx} className="p-8 space-y-4 hover:bg-slate-900/40 transition-colors group relative">
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                              <div className="flex items-start justify-between gap-8">
                                <div className="space-y-4 flex-1">
                                  <p className="text-slate-200 italic leading-relaxed text-[15px] font-medium">
                                    "{item.instruction}"
                                  </p>
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                                      <UserCheck size={10} className="text-emerald-500" />
                                      <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">{item.assigned_to || "System Agent"}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1 bg-slate-800 rounded-full border border-slate-700">
                                      <Clock size={10} className="text-slate-500" />
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        {new Date(item.date).toLocaleString('en-US', {
                                          day: '2-digit',
                                          month: 'short',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="shrink-0 flex flex-col items-end">
                                  <div className="w-10 h-10 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-emerald-500/50">
                                    <CheckCircle2 size={18} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-16 text-center">
                          <div className="w-16 h-16 bg-slate-900 rounded-3xl flex items-center justify-center text-slate-800 mx-auto mb-6 border border-slate-800 shadow-inner"><MessageSquare size={32} /></div>
                          <p className="text-slate-600 font-black uppercase tracking-[0.2em] text-[12px]">No historical interaction logs found for this node.</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Interests & Matches Intelligence */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Engagement & Interests Section */}
                  <div className="space-y-6">
                    <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><Heart size={14} className="text-pink-500" /> Interests & Engagement</h3>
                    <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
                      {Array.isArray(selectedUserDetails.likes) && selectedUserDetails.likes.length > 0 ? (
                        <div className="divide-y divide-slate-800/50 max-h-[400px] overflow-y-auto custom-scrollbar">
                          {selectedUserDetails.likes.map((item: any, idx: number) => (
                            <div key={idx} className="p-6 hover:bg-slate-900/40 transition-colors group">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${item.liked ? "bg-pink-500/10 border-pink-500/20 text-pink-500" : item.disliked ? "bg-red-500/10 border-red-500/20 text-red-500" : "bg-slate-900 border-slate-800 text-slate-600"}`}>
                                    {item.liked ? <Heart size={18} fill="currentColor" /> : item.disliked ? <UserMinus size={18} /> : <Users size={18} />}
                                  </div>
                                  <div>
                                    <div className="text-[13px] font-black text-white uppercase tracking-tight group-hover:text-pink-400 transition-colors">{item.name || "Anonymous Profile"}</div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                      <div className="text-[9px] font-mono text-slate-500 uppercase flex items-center gap-1">
                                        <span className="text-slate-700">ID:</span> {item.matrimonial_id}
                                      </div>
                                      <div className="w-1 h-1 bg-slate-800 rounded-full hidden sm:block" />
                                      <div className="text-[9px] font-bold text-slate-500 uppercase flex items-center gap-1">
                                        <Clock size={8} /> {item.created_at ? new Date(item.created_at).toLocaleString('en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : "No Timestamp"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${item.liked ? "bg-pink-500/20 text-pink-400" : item.disliked ? "bg-red-500/20 text-red-400" : "bg-slate-800 text-slate-500"}`}>
                                    {item.liked ? "Liked Interest" : item.disliked ? "Rejected" : "Viewed"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-12 text-center">
                          <Heart size={24} className="text-slate-800 mx-auto mb-4" />
                          <p className="text-slate-600 font-black uppercase tracking-[0.2em] text-[10px]">No interest patterns recorded.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Matching Pipeline Section */}
                  <div className="space-y-6">
                    <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><Sparkles size={14} className="text-blue-500" /> Profile Matching Engine</h3>
                    <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
                      {Array.isArray(selectedUserDetails.matches) && selectedUserDetails.matches.length > 0 ? (
                        <div className="divide-y divide-slate-800/50 max-h-[400px] overflow-y-auto custom-scrollbar">
                          {[...selectedUserDetails.matches].sort((a: any, b: any) => {
                            const dateA = new Date(a.created_at).getTime();
                            const dateB = new Date(b.created_at).getTime();
                            if (dateB !== dateA) return dateB - dateA;
                            return (b.score || 0) - (a.score || 0);
                          }).map((item: any, idx: number) => (
                            <div key={idx} className="p-6 hover:bg-slate-900/40 transition-colors group">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                                    <Sparkles size={18} />
                                  </div>
                                  <div>
                                    <div className="text-[13px] font-black text-white uppercase tracking-tight">{item.candidate_id}</div>
                                    <div className="text-[9px] font-mono text-slate-600 uppercase mt-0.5">
                                      Match Score: <span className="text-blue-400 font-bold">{item.score}</span> • {item.published ? <span className="text-emerald-500">Published</span> : <span className="text-amber-500">Not Published</span>}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-1">Created</div>
                                  <div className="text-[11px] font-bold text-slate-400">
                                    {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-12 text-center">
                          <Sparkles size={24} className="text-slate-800 mx-auto mb-4" />
                          <p className="text-slate-600 font-black uppercase tracking-[0.2em] text-[10px]">No active match pipelines detected.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Followup Intelligence */}
                <div className="space-y-6">
                  <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><History size={14} className="text-blue-500" /> Awaiting Followup Audit</h3>
                  <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
                    {Array.isArray(selectedUserDetails.awaiting_followup) && selectedUserDetails.awaiting_followup.length > 0 ? (
                      <div className="divide-y divide-slate-800/50">
                        {selectedUserDetails.awaiting_followup.map((item: any, idx: number) => (
                          <div key={idx} className="p-8 space-y-6 hover:bg-slate-900/40 transition-colors group">
                            <div className="flex items-start justify-between gap-8">
                              <div className="space-y-3 flex-1">
                                <p className="text-slate-200 italic leading-relaxed text-[15px] font-medium">
                                  "{item.instruction || item.followup_instruction}"
                                </p>
                                <div className="flex items-center gap-4">
                                  <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/10 rounded-full">
                                    <UserCheck size={10} className="text-blue-500" />
                                    <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">{item.admin || "System Agent"}</span>
                                  </div>
                                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-800 rounded-full">
                                    <Clock size={10} className="text-slate-500" />
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.date || 'Historical Log'}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-1 flex items-center justify-end gap-2"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" /> Scheduled Feedback</div>
                                <div className="text-[14px] font-black text-white italic tracking-tighter uppercase">{item.followup_date || 'TBD'}</div>
                                {item.followup_time !== '00:00:00' && <div className="text-[10px] font-bold text-slate-500 mt-1">{item.followup_time}</div>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-16 text-center">
                        <div className="w-16 h-16 bg-slate-900 rounded-3xl flex items-center justify-center text-slate-800 mx-auto mb-6 border border-slate-800 shadow-inner"><History size={32} /></div>
                        <p className="text-slate-600 font-black uppercase tracking-[0.2em] text-[12px]">No active followup intelligence sequences found.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Audit Trail */}
                <div className="space-y-12">
                  {/* Success Section */}
                  <div className="space-y-6">
                    <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><Download size={14} className="text-emerald-500" /> Success Ledger Audit</h3>
                    <div className="space-y-4">
                      {(selectedUserDetails.transactions?.success_details?.length || 0) > 0 ? (
                        selectedUserDetails.transactions.success_details.map((tx: any, i: number) => (
                          <div key={i} className="bg-slate-950/50 border border-slate-800 rounded-3xl p-8 flex flex-col md:flex-row md:items-center justify-between gap-8 hover:border-emerald-500/20 transition-all shadow-lg">
                            <div className="flex items-center gap-6">
                              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/20 shadow-inner">
                                <TrendingUp size={24} />
                              </div>
                              <div>
                                <div className="text-[15px] font-black text-white uppercase tracking-tight">{tx.description || "Credit Activation"}</div>
                                <div className="text-[11px] font-mono text-slate-600 mt-1 flex items-center gap-2">LOG_ID: <span className="text-slate-400">{tx.transaction_id}</span></div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-12">
                              <div className="text-right">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Monetary Value</div>
                                <div className="text-xl font-black text-white italic tracking-tighter">₹{tx.amount}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Credit Units</div>
                                <div className="text-xl font-black text-emerald-400 italic tracking-tighter">+{tx.credits}</div>
                              </div>
                              <div className="text-right min-w-[140px]">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Finalized At</div>
                                <div className="text-[13px] font-bold text-slate-300 uppercase tracking-tight">{tx.date ? new Date(tx.date).toLocaleString() : 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-16 text-center border-2 border-dashed border-slate-800 rounded-[2.5rem] bg-slate-950/20">
                          <p className="text-[12px] font-black text-slate-700 uppercase tracking-[0.3em]">No successful transaction nodes detected in history.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Failed Section */}
                  <div className="space-y-6">
                    <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><XCircle size={14} className="text-red-500" /> Failed Attempts Audit</h3>
                    <div className="space-y-4">
                      {(selectedUserDetails.transactions?.failed_details?.length || 0) > 0 ? (
                        selectedUserDetails.transactions.failed_details.map((tx: any, i: number) => (
                          <div key={i} className="bg-slate-950/50 border border-slate-800 rounded-3xl p-8 flex flex-col md:flex-row md:items-center justify-between gap-8 hover:border-red-500/20 transition-all shadow-lg">
                            <div className="flex items-center gap-6">
                              <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center text-red-500 border border-red-500/20 shadow-inner">
                                <TrendingDown size={24} />
                              </div>
                              <div>
                                <div className="text-[15px] font-black text-white uppercase tracking-tight">{tx.description || "Failed Credit Purchase"}</div>
                                <div className="text-[11px] font-mono text-slate-600 mt-1 flex items-center gap-2">LOG_ID: <span className="text-slate-400">{tx.transaction_id || 'N/A'}</span></div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-12">
                              <div className="text-right">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Attempted Value</div>
                                <div className="text-xl font-black text-white italic tracking-tighter">₹{tx.amount}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Target Credits</div>
                                <div className="text-xl font-black text-red-400 italic tracking-tighter">{tx.credits}</div>
                              </div>
                              <div className="text-right min-w-[140px]">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Attempted At</div>
                                <div className="text-[13px] font-bold text-slate-300 uppercase tracking-tight">{tx.date ? new Date(tx.date).toLocaleString() : 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-16 text-center border-2 border-dashed border-slate-800 rounded-[2.5rem] bg-slate-950/20">
                          <p className="text-[12px] font-black text-slate-700 uppercase tracking-[0.3em]">No failed transaction attempts detected in history.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pending Section */}
                  <div className="space-y-6">
                    <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] flex items-center gap-3"><Clock size={14} className="text-amber-500" /> Pending Actions Audit</h3>
                    <div className="space-y-4">
                      {(selectedUserDetails.transactions?.pending_details?.length || 0) > 0 ? (
                        selectedUserDetails.transactions.pending_details.map((tx: any, i: number) => (
                          <div key={i} className="bg-slate-950/50 border border-slate-800 rounded-3xl p-8 flex flex-col md:flex-row md:items-center justify-between gap-8 hover:border-amber-500/20 transition-all shadow-lg">
                            <div className="flex items-center gap-6">
                              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500 border border-amber-500/20 shadow-inner">
                                <Activity size={24} />
                              </div>
                              <div>
                                <div className="text-[15px] font-black text-white uppercase tracking-tight">{tx.description || "Pending Purchase"}</div>
                                <div className="text-[11px] font-mono text-slate-600 mt-1 flex items-center gap-2">MERCHANT_ID: <span className="text-slate-400">{tx.merchant_id || 'N/A'}</span></div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-12">
                              <div className="text-right">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Attempted Value</div>
                                <div className="text-xl font-black text-white italic tracking-tighter">₹{tx.amount}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Target Credits</div>
                                <div className="text-xl font-black text-amber-400 italic tracking-tighter">{tx.credits}</div>
                              </div>
                              <div className="text-right min-w-[140px]">
                                <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Initiated At</div>
                                <div className="text-[13px] font-bold text-slate-300 uppercase tracking-tight">{tx.date ? new Date(tx.date).toLocaleString() : 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-16 text-center border-2 border-dashed border-slate-800 rounded-[2.5rem] bg-slate-950/20">
                          <p className="text-[12px] font-black text-slate-700 uppercase tracking-[0.3em]">No pending transaction attempts detected.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-8 bg-slate-950 border-t border-slate-800 flex items-center justify-between shadow-2xl">
                <div className="flex items-center gap-3 text-slate-600">
                  <Shield size={14} />
                  <span className="text-[10px] font-black uppercase tracking-widest">End-to-End Encrypted Audit Trail</span>
                </div>
                <button
                  onClick={() => setSelectedUserDetails(null)}
                  className="px-12 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl text-[13px] font-black uppercase tracking-[0.2em] transition-all shadow-xl shadow-blue-600/20 active:scale-95"
                >
                  Terminate Audit View
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
